import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Copy, Check, CheckCircle, RefreshCw, Wand2, ChevronDown, Search, Clock, Globe, Target, AlertCircle, ArrowRight, RotateCcw } from 'lucide-react';
import { BrandKit, CTAGenerationResult } from '../types';
import googleDocsService from '../services/googleDocsService';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';
import { sanitizeHTMLForDisplay } from '../utils/htmlSanitizer';

// Skeleton component for loading states (copied from BlogContentActionModal)
const Skeleton = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div
    className={className}
    style={{
      backgroundColor: '#e5e7eb',
      borderRadius: '0.25rem',
      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
      ...style
    }}
  />
);

interface CompetitorRowBasic {
  id: string;
  keyword: string;
  url: string;
  status: 'idle' | 'queued' | 'running' | 'completed' | 'error';
  output?: string;
  createdAt: string;
  metadata?: {
    title: string;
    description: string;
    metaSeoTitle?: string;
    metaDescription?: string;
    word_count: number;
    seo_optimized: boolean;
    citations_included: boolean;
    brand_variables_processed: number;
    aeo_optimized: boolean;
  };
  generationResult?: any;
  workflowDetails?: {
    firecrawl?: any;
    deepResearch?: any;
    gapAnalysis?: any;
    contentGeneration?: any;
  };
}

interface CompetitorContentActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: CompetitorRowBasic;
  brandKit?: BrandKit | null;
  onContentUpdate?: (rowId: string, newContent: string) => void;
}

// CTA-related interfaces (copied from BlogContentActionModal)
interface CTAVariant {
  position: 'beginning' | 'middle' | 'end';
  cta: {
    category_header: string;
    headline: string;
    description: string;
    action_button: string;
  };
  strategy: string;
  shortcode: string;
}

/**
 * Variables Menu Component (duplicated from BlogContentActionModal for UI parity)
 * Why this matters: Provides a searchable popup for inserting brand variables into prompts,
 * matching the Blog modal UX to reduce cognitive load.
 */
const VariablesMenu: React.FC<{
  showVariablesMenu: boolean;
  variablesButtonPosition: { top: number; left: number } | null;
  variablesMenuRef: React.RefObject<HTMLDivElement | null>;
  activePromptField: 'system' | 'user';
  variableSearchTerm: string;
  setVariableSearchTerm: (term: string) => void;
  setShowVariablesMenu: (show: boolean) => void;
  brandKit: BrandKit | null;
  insertVariable: (value: string) => void;
}> = ({
  showVariablesMenu,
  variablesButtonPosition,
  variablesMenuRef,
  activePromptField,
  variableSearchTerm,
  setVariableSearchTerm,
  setShowVariablesMenu,
  brandKit,
  insertVariable
}) => {
  if (!showVariablesMenu || !variablesButtonPosition) return null;

  // Build variables list including custom variables
  const getAllVariables = () => {
    const standardVariables = [
      { key: 'url', label: 'Brand URL', value: '{{ brand_kit.url }}' },
      { key: 'about_brand', label: 'About Brand', value: '{{ brand_kit.about_brand }}' },
      { key: 'ideal_customer_profile', label: 'Ideal Customer Profile', value: '{{ brand_kit.ideal_customer_profile }}' },
      { key: 'competitors', label: 'Competitors', value: '{{ brand_kit.competitors }}' },
      { key: 'brand_point_of_view', label: 'Brand Point of View', value: '{{ brand_kit.brand_point_of_view }}' },
      { key: 'author_persona', label: 'Author Persona', value: '{{ brand_kit.author_persona }}' },
      { key: 'tone_of_voice', label: 'Tone of Voice', value: '{{ brand_kit.tone_of_voice }}' },
      { key: 'header_case_type', label: 'Header Case Type', value: '{{ brand_kit.header_case_type }}' },
      { key: 'writing_rules', label: 'Writing Rules', value: '{{ brand_kit.writing_rules }}' },
      { key: 'cta_text', label: 'CTA Text', value: '{{ brand_kit.cta_text }}' },
      { key: 'cta_destination', label: 'CTA Destination', value: '{{ brand_kit.cta_destination }}' },
      // Competitor-conquesting extras (empty by default, can be filled upstream)
      { key: 'research_key_insights', label: 'Research Insights', value: '{{ brand_kit.research_key_insights }}' },
      { key: 'gap_analysis_summary', label: 'Gap Analysis Summary', value: '{{ brand_kit.gap_analysis_summary }}' },
      { key: 'primary_angle', label: 'Primary Angle', value: '{{ brand_kit.primary_angle }}' },
      { key: 'structure_recommendations', label: 'Structure Recs', value: '{{ brand_kit.structure_recommendations }}' },
      { key: 'seo_suggestions', label: 'SEO Suggestions', value: '{{ brand_kit.seo_suggestions }}' }
    ];

    const customVariables = brandKit && brandKit.customVariables
      ? Object.keys(brandKit.customVariables).map((key) => ({
          key,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          value: `{{ brand_kit.${key} }}`
        }))
      : [];

    return [...customVariables, ...standardVariables];
  };

  const filtered = (() => {
    const all = getAllVariables();
    if (!variableSearchTerm) return all;
    return all.filter(
      (v) =>
        v.label.toLowerCase().includes(variableSearchTerm.toLowerCase()) ||
        v.key.toLowerCase().includes(variableSearchTerm.toLowerCase())
    );
  })();

  return (
    <div
      ref={variablesMenuRef}
      className="content-variables-menu"
      style={{
        position: 'fixed',
        top: variablesButtonPosition.top,
        left: variablesButtonPosition.left,
        backgroundColor: 'white',
        border: '0.0625rem solid #e5e7eb',
        borderRadius: '0.75rem',
        boxShadow: '0 1.5625rem 3.125rem -0.75rem rgba(0, 0, 0, 0.25)',
        zIndex: 10000,
        width: '25rem',
        maxHeight: '37.5rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #f3f4f6', background: '#fafafa' }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: '#111827' }}>Brand Variables</h3>
          <button
            onClick={() => setShowVariablesMenu(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', borderRadius: '0.25rem' }}
          >
            <X size={20} />
          </button>
        </div>
        <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>Insert into {activePromptField} prompt</p>
      </div>

      <div style={{ padding: '1rem 1.5rem' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search variables..."
            value={variableSearchTerm}
            onChange={(e) => setVariableSearchTerm(e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              padding: '0.625rem 0.625rem 0.625rem 2.5rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>
        {filtered.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {filtered.map((variable) => (
              <div
                key={variable.key}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); insertVariable(variable.value); }}
                style={{
                  padding: '1rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  background: 'white'
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem', color: '#374151' }}>{variable.label}</div>
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: '#7c3aed',
                    backgroundColor: '#f3f4f6',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    border: '1px solid #e5e7eb',
                    fontWeight: 600
                  }}
                >
                  {variable.value}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
            <Search size={32} style={{ margin: '0 auto 1rem', opacity: 0.3, display: 'block' }} />
            <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>No variables found</p>
            <p style={{ fontSize: '0.75rem', margin: 0 }}>Try adjusting your search terms</p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * CompetitorContentActionModal
 * Why this matters: Provides tailored prompts and regeneration tools for the competitor-conquesting flow
 * (explicitly beating a specific competitor URL) without losing power-user features like copying and editing.
 */
const CompetitorContentActionModal: React.FC<CompetitorContentActionModalProps> = ({
  isOpen,
  onClose,
  row,
  brandKit = null,
  onContentUpdate
}) => {
  // Prompts & brand kit
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [resolvedBrandKit, setResolvedBrandKit] = useState<BrandKit | null>(brandKit || null);
  
  // Store workflow data (deep research and gap analysis) from previous generation
  const [workflowData, setWorkflowData] = useState<{
    deep_research?: any;
    gap_analysis?: any;
  } | null>(null);

  // UI states
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [showVariablesMenu, setShowVariablesMenu] = useState<boolean>(false);
  const [activePromptField, setActivePromptField] = useState<'system' | 'user'>('system');
  const [variableSearchTerm, setVariableSearchTerm] = useState<string>('');
  const [variablesButtonPosition, setVariablesButtonPosition] = useState<{ top: number; left: number } | null>(null);

  // Content & editing
  const [generatedContent, setGeneratedContent] = useState<string>('');
  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [editableContent, setEditableContent] = useState<string>('');
  // Meta SEO fields and copy UI state (parity with Blog modal)
  const [metaSeoTitle, setMetaSeoTitle] = useState<string>('');
  const [metaDescription, setMetaDescription] = useState<string>('');
  const [showMetaTitleCopied, setShowMetaTitleCopied] = useState<boolean>(false);
  const [showMetaDescCopied, setShowMetaDescCopied] = useState<boolean>(false);
  // Auto-save prompts UI state
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saving' | 'saved' | ''>('');
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [hasUserInput, setHasUserInput] = useState<boolean>(false);
  // Sheets UX
  const [isOpeningSheets, setIsOpeningSheets] = useState<boolean>(false);
  const [showSheetsMessage, setShowSheetsMessage] = useState<boolean>(false);
  const [sheetsSuccessMessage, setSheetsSuccessMessage] = useState<string>('');

  // CMS Integration states
  const [showCMSModal, setShowCMSModal] = useState(false);
  const [showCustomCMSForm, setShowCustomCMSForm] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [publishResult, setPublishResult] = useState<any>(null);
  const [showComingSoonMessage, setShowComingSoonMessage] = useState<string | null>(null);
  const [customCMSConfig, setCustomCMSConfig] = useState({
    api_endpoint: 'https://api.buttercms.com/v2',
    api_key: '',
    cms_type: 'buttercms'
  });
  
  // Contextual CTA states
  const [enableContextualCtas, setEnableContextualCtas] = useState(true);
  const [isEnhancingWithCtas, setIsEnhancingWithCtas] = useState(false);
  const [ctaEnhancementResult, setCtaEnhancementResult] = useState<any>(null);

  // CTA Generation states (copied from BlogContentActionModal)
  const [generatedCTAs, setGeneratedCTAs] = useState<any>(null);
  const [isGeneratingCTAs, setIsGeneratingCTAs] = useState(false);
  const [ctaError, setCtaError] = useState('');
  const [ctaGenerationStage, setCtaGenerationStage] = useState('');
  const [showCtaSkeletons, setShowCtaSkeletons] = useState(false);
  const [ctaCopySuccess, setCtaCopySuccess] = useState<string | null>(null);
  const [vocKitReady, setVocKitReady] = useState(false);
  const [vocKitReadyDismissed, setVocKitReadyDismissed] = useState(false);
  const [painPointsCount, setPainPointsCount] = useState(0);

  // Edit history functionality for CTA insertion (copied from BlogContentActionModal)
  const [editHistory, setEditHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Refs
  const systemRef = useRef<HTMLTextAreaElement | null>(null);
  const userRef = useRef<HTMLTextAreaElement | null>(null);
  const variablesMenuRef = useRef<HTMLDivElement | null>(null);
  const systemVariablesButtonRef = useRef<HTMLButtonElement | null>(null);
  const userVariablesButtonRef = useRef<HTMLButtonElement | null>(null);
  const editableContentRef = useRef<HTMLTextAreaElement | null>(null);
  const rightPanelRef = useRef<HTMLDivElement | null>(null);

  /**
   * generateAIMetaFields
   * Why this matters: Produces meta SEO fields so this modal mirrors Blog modal UX.
   */
  function generateAIMetaFields(
    keyword: string,
    content: string
  ): Promise<{ metaSeoTitle: string; metaDescription: string }> {
    return (async () => {
      try {
        const contentPreview = content.replace(/<[^>]*>/g, '').substring(0, 500);
        const apiUrl = API_ENDPOINTS.generateMeta;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            keyword,
            content_preview: contentPreview,
            prompt: `Generate meta fields for competitor content about "${keyword}".`
          })
        });
        if (!response.ok) throw new Error('Meta API failed');
        const data = await response.json();
        return {
          metaSeoTitle: data.metaSeoTitle || generateFallbackTitle(keyword),
          metaDescription: data.metaDescription || generateFallbackDescription(keyword)
        };
      } catch {
        return {
          metaSeoTitle: generateFallbackTitle(keyword),
          metaDescription: generateFallbackDescription(keyword)
        };
      }
    })();
  }

  /**
   * Fallback meta helpers
   * Why this matters: Guarantees fields even if AI meta API fails.
   */
  function generateFallbackTitle(keyword: string): string {
    const k = keyword.trim();
    // AEO-optimized fallback: question format or clear topic statement
    // Why this matters: AI engines prefer clear, intent-matching titles without hyperbole
    console.log('⚠️ Using fallback title generation for keyword:', k);
    if (k.toLowerCase().startsWith('what') || k.toLowerCase().startsWith('how')) {
      return `${k.charAt(0).toUpperCase() + k.slice(1)} | US Citrus AI`;
    }
    // Use more varied, natural formats
    const formats = [
      `${k.charAt(0).toUpperCase() + k.slice(1)} for B2B Teams | US Citrus AI`,
      `Understanding ${k.charAt(0).toUpperCase() + k.slice(1)} in Sales | US Citrus AI`,
      `${k.charAt(0).toUpperCase() + k.slice(1)}: Practical Guide | US Citrus AI`
    ];
    return formats[Math.floor(Math.random() * formats.length)];
  }
  function generateFallbackDescription(keyword: string): string {
    // AEO-optimized fallback: direct value statement without made-up claims
    // Why this matters: AI engines penalize unverifiable claims and prefer factual descriptions
    const k = keyword.trim().toLowerCase();
    console.log('⚠️ Using fallback description generation for keyword:', k);
    // Use more varied, natural descriptions
    const templates = [
      `Explore ${k} strategies and implementation approaches for B2B sales teams. US Citrus AI provides tools and insights to optimize your ${k} processes.`,
      `Practical ${k} techniques for sales organizations. See how US Citrus AI's platform enables teams to implement and scale ${k} effectively.`,
      `${k.charAt(0).toUpperCase() + k.slice(1)} fundamentals and best practices for modern sales teams. US Citrus AI helps streamline ${k} workflows and drive results.`
    ];
    // Note: For SEO, descriptions should ideally be 150-160 characters, but we show full content in UI for user review
    return templates[Math.floor(Math.random() * templates.length)];
  }

  /**
   * Get random CTA anchor text to ensure even distribution
   * Why this matters: Prevents LLM bias toward first option in the list
   */
  const getRandomCTAAnchorText = (): string => {
    const ctaOptions = [
      "Start Your Free Trial",
      "Try US Citrus AI", 
      "Start a Trial",
      "Schedule a Demo",
      "Request a Demo", 
      "Start Prospecting",
      "Get Leads Now"
    ];
    
    // Use random selection to ensure even distribution
    const randomIndex = Math.floor(Math.random() * ctaOptions.length);
    const selectedCTA = ctaOptions[randomIndex];
    console.log(`🎯 [CompetitorModal] Selected CTA anchor text: "${selectedCTA}" (${randomIndex + 1}/${ctaOptions.length})`);
    return selectedCTA;
  };

  // Load initial content from row
  useEffect(() => {
    if (!isOpen) return;
    // Clean the content to remove JSON artifacts and escape sequences
    const cleanedContent = cleanAIContent(row.output || '');
    setGeneratedContent(cleanedContent);
    setEditableContent(cleanedContent);
    
    // Load workflow data from localStorage if available
    try {
      const savedWorkflow = localStorage.getItem(`us_citrus_ai_competitor_workflow_${row.id}`);
      if (savedWorkflow) {
        const parsedWorkflow = JSON.parse(savedWorkflow);
        console.log('📊 Loaded saved workflow data for row:', row.id);
        setWorkflowData(parsedWorkflow);
      }
    } catch (e) {
      console.error('Failed to load workflow data:', e);
    }
    
    // Load saved CTAs (same pattern as BlogContentActionModal)
    try {
      const savedCTAs = localStorage.getItem(`us_citrus_ai_competitor_ctas_${row.id}`);
      if (savedCTAs) {
        const parsedCTAs = JSON.parse(savedCTAs);
        setGeneratedCTAs(parsedCTAs);
        console.log('✅ [CompetitorModal] Loaded saved CTAs for row:', row.id, 'CTA variants:', Object.keys(parsedCTAs.cta_variants || {}));
      } else {
        console.log('ℹ️ [CompetitorModal] No saved CTAs found for row:', row.id);
      }
    } catch (error) {
      console.error('❌ [CompetitorModal] Error loading saved CTAs:', error);
    }
    
        // Load saved meta fields from localStorage if they exist
    // We no longer auto-generate meta fields - they come from content generation
    const content = row.output || '';
    if (content.trim().length > 0 && !metaSeoTitle && !metaDescription) {
      // Check if meta fields are already in row.metadata (from backend)
      if ((row.metadata as any)?.metaSeoTitle || (row.metadata as any)?.metaDescription) {
        // Already set from metadata, skip loading from localStorage
        return;
      }
      
      // Load from localStorage if previously saved
      try {
        const savedMeta = localStorage.getItem(`us_citrus_ai_competitor_meta_${row.id}`);
        if (savedMeta) {
          const { metaSeoTitle: savedTitle, metaDescription: savedDesc } = JSON.parse(savedMeta);
          if (savedTitle || savedDesc) {
            setMetaSeoTitle(savedTitle || '');
            setMetaDescription(savedDesc || '');
            return;
          }
        }
      } catch {}
    }
  }, [isOpen, row.output]);

  // Load brand kit if not provided via props (draft first, then saved). Keep in sync with Blog/Content modals.
  useEffect(() => {
    const loadBrandKit = () => {
      if (brandKit) {
        setResolvedBrandKit(brandKit);
        return;
      }
      try {
        const draft = localStorage.getItem('us_citrus_ai_brand_kit_draft');
        const saved = localStorage.getItem('us_citrus_ai_brand_kit');
        const data = draft || saved;
        if (data) {
          setResolvedBrandKit(JSON.parse(data));
        } else {
          setResolvedBrandKit(null);
        }
      } catch {
        setResolvedBrandKit(null);
      }
    };
    loadBrandKit();
  }, [brandKit]);

  // Listen for brand kit updates (same-tab custom event and storage) and refresh immediately
  useEffect(() => {
    const loadBK = () => {
      try {
        const draft = localStorage.getItem('us_citrus_ai_brand_kit_draft');
        const saved = localStorage.getItem('us_citrus_ai_brand_kit');
        const data = draft || saved;
        setResolvedBrandKit(data ? JSON.parse(data) : null);
      } catch {
        setResolvedBrandKit(null);
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'us_citrus_ai_brand_kit' || e.key === 'us_citrus_ai_brand_kit_draft') loadBK();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('us-citrus-ai-brand-kit-updated', loadBK as any);
    // Initial sync on mount of this effect
    loadBK();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('us-citrus-ai-brand-kit-updated', loadBK as any);
    };
  }, []);

  // Load saved prompts if present, else keep generated defaults
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = localStorage.getItem(`us_citrus_ai_competitor_prompts_draft_${row.id}`);
      if (saved) {
        const { systemPrompt: savedSystem, userPrompt: savedUser } = JSON.parse(saved);
        if (typeof savedSystem === 'string') setSystemPrompt(savedSystem);
        if (typeof savedUser === 'string') setUserPrompt(savedUser);
      }
    } catch (err) {
      // ignore bad JSON
      console.warn('[CompetitorContentActionModal] Failed to load saved prompts', err);
    }
  }, [isOpen, row.id]);

  // Auto-save prompts when they change (debounced)
  useEffect(() => {
    if (!isOpen || !hasUserInput) return;
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    setAutoSaveStatus('saving');
    const timeout = setTimeout(() => {
      try {
        const promptsData = {
          systemPrompt,
          userPrompt,
          rowId: row.id,
          lastModified: new Date().toISOString()
        };
        localStorage.setItem(`us_citrus_ai_competitor_prompts_draft_${row.id}`, JSON.stringify(promptsData));
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      } catch {
        setAutoSaveStatus('');
      }
    }, 1000);
    setAutoSaveTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [systemPrompt, userPrompt, isOpen, hasUserInput, row.id]);

  // Clear auto-save state on close
  useEffect(() => {
    if (!isOpen) {
      setAutoSaveStatus('');
      setHasUserInput(false);
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
        setAutoSaveTimeout(null);
      }
    }
  }, [isOpen, autoSaveTimeout]);

  // VoC Kit status checking (copied from BlogContentActionModal)
  useEffect(() => {
    const checkVocKit = () => {
      try {
        // Check draft first, then fallback to saved version (same as VoCKitPage)
        const draft = localStorage.getItem('us_citrus_ai_voc_kit_draft');
        const saved = localStorage.getItem('us_citrus_ai_voc_kit');
        const vocData = draft || saved;
        
        console.log('🔍 CompetitorContentActionModal VoC Kit Check:', {
          hasDraft: !!draft,
          hasSaved: !!saved,
          usingData: vocData ? 'found' : 'none'
        });
        
        if (vocData) {
          const parsed = JSON.parse(vocData);
          const hasAnalysis = parsed.hasGeneratedAnalysis && parsed.extractedPainPoints?.length > 0;
          
          console.log('📊 VoC Kit Data Analysis:', {
            hasGeneratedAnalysis: parsed.hasGeneratedAnalysis,
            painPointsCount: parsed.extractedPainPoints?.length || 0,
            finalVocKitReady: hasAnalysis
          });
          
          setVocKitReady(hasAnalysis);
          setPainPointsCount(parsed.extractedPainPoints?.length || 0);
        } else {
          console.log('❌ No VoC Kit data found');
          setVocKitReady(false);
          setPainPointsCount(0);
        }
      } catch (error) {
        console.error('❌ Error checking VoC Kit:', error);
        setVocKitReady(false);
        setPainPointsCount(0);
      }
    };

    const checkDismissedState = () => {
      try {
        const dismissed = localStorage.getItem('us_citrus_ai_voc_kit_ready_dismissed');
        setVocKitReadyDismissed(dismissed === 'true');
      } catch {
        setVocKitReadyDismissed(false);
      }
    };

    if (isOpen) {
      checkVocKit();
      checkDismissedState();
    }

    const handleVocUpdate = () => checkVocKit();
    window.addEventListener('us-citrus-ai-voc-kit-updated', handleVocUpdate);
    return () => window.removeEventListener('us-citrus-ai-voc-kit-updated', handleVocUpdate);
  }, [isOpen]);

  /**
   * defaultSystemPrompt / defaultUserPrompt
   * Why this matters: Prompts are tailored to “beat the competitor URL” using deep research and gap analysis.
   */
  const defaultSystemPrompt = useMemo(() => {
    return `You are an elite content strategist and writer. Your goal is to generate a comprehensive, AEO-optimized article that explicitly outperforms a specific competitor page for the target keyword. You must:
- Cover all core topics the competitor covers, but with clearer structure and more depth
- Incorporate unique insights from independent deep research
- Address gaps identified by gap analysis
- Produce clean markdown with proper H1/H2/H3, tables when helpful, and inline citations [anchor](URL)
- End with a strong conclusion and clear CTA to try US Citrus AI for free`;
  }, []);

  const defaultUserPrompt = useMemo(() => {
    // Select random CTA to prevent LLM bias
    const selectedCTA = getRandomCTAAnchorText();
    
    // Build deep research and gap analysis context if available
    let researchContext = '';
    if (workflowData?.deep_research) {
      const insights = workflowData.deep_research?.research_findings?.key_insights || [];
      if (insights.length > 0) {
        researchContext += `\nDEEP RESEARCH INSIGHTS:\n${JSON.stringify(insights, null, 2)}\n`;
      }
    }
    if (workflowData?.gap_analysis) {
      const gaps = workflowData.gap_analysis?.analysis_summary?.identified_gaps || [];
      const strategy = workflowData.gap_analysis?.content_strategy || {};
      if (gaps.length > 0 || strategy.primary_angle) {
        researchContext += `\nGAP ANALYSIS:\n- Identified Gaps: ${JSON.stringify(gaps, null, 2)}`;
        if (strategy.primary_angle) {
          researchContext += `\n- Primary Angle: ${strategy.primary_angle}`;
        }
        if (strategy.content_structure_recommendations) {
          researchContext += `\n- Structure Recommendations: ${JSON.stringify(strategy.content_structure_recommendations, null, 2)}`;
        }
        researchContext += '\n';
      }
    }
    
    return `OBJECTIVE: Outperform this competitor page for "${row.keyword}": ${row.url}

TARGET AUDIENCE: {{ brand_kit.ideal_customer_profile }}
BRAND CONTEXT: {{ brand_kit.about_brand }}
COMPETITORS: {{ brand_kit.competitors }}
BRAND POV: {{ brand_kit.brand_point_of_view }}${researchContext ? '\n' + researchContext : ''}

CONTENT REQUIREMENTS:
1) Analyze and outperform the competitor URL above
2) Use {{ brand_kit.tone_of_voice }} tone throughout
3) Follow these writing rules: {{ brand_kit.writing_rules }}
4) Apply {{ brand_kit.header_case_type }} for headers
5) Include substantial unique insights that competitor lacks${workflowData ? ' (leverage deep research insights above)' : ''}
6) Use markdown with proper headers, lists, and tables
7) Include inline hyperlink citations
8) End with "Getting Started with ${row.keyword}" section
9) Include CTA using this exact anchor text: "${selectedCTA}" - always link to the UTM-tracked US Citrus AI signup URL${workflowData ? '\n10) Address all identified gaps from the gap analysis above' : ''}

The content should be comprehensive, authoritative, and clearly superior to the competitor page.`;
  }, [row.keyword, row.url, workflowData]);

  /**
   * generateInitialPrompts
    * Why this matters: Ensures competitor context and deep research/gap analysis outputs are
    * explicitly injected into the prompts, and brand kit variables are required throughout.
    * Produces consistent, JSON-structured output (content + meta fields) and formatting.
   */
  const generateInitialPrompts = () => {
    const currentYear = 2025;
    // Select random CTA to prevent LLM bias
    const selectedCTA = getRandomCTAAnchorText();
    // Extract workflow data from the row
    const workflowData = row.workflowDetails ? {
      deep_research: row.workflowDetails.deepResearch,
      gap_analysis: row.workflowDetails.gapAnalysis
    } : null;
    
    // Debug log to see what workflow data is available
    if (workflowData) {
      console.log('🔬 Workflow data available for prompt generation:', {
        hasDeepResearch: !!workflowData.deep_research,
        hasGapAnalysis: !!workflowData.gap_analysis,
        deepResearchKeys: workflowData.deep_research ? Object.keys(workflowData.deep_research) : [],
        gapAnalysisKeys: workflowData.gap_analysis ? Object.keys(workflowData.gap_analysis) : []
      });
    } else {
      console.log('📝 No workflow data available for this row');
    }
    
    const systemPromptTemplate = `You are a world-class SEO, AEO, and LLM SEO content marketer for US Citrus AI with deep expertise in creating comprehensive, AI-optimized articles that rank highly and get cited by AI answer engines (ChatGPT, Perplexity, Gemini, Claude, etc.). Your specialty is transforming content briefs into definitive resources that become the go-to sources for specific topics.

CRITICAL CONTENT PHILOSOPHY:
Your goal is to create content that becomes the definitive, comprehensive resource on the topic - the content that other creators reference and that AI engines cite as authoritative.

CONTENT COVERAGE REQUIREMENTS:
- Address ALL aspects of the topic comprehensively
- Include practical, actionable guidance that readers can implement
- Provide genuine value that advances knowledge in the space
- Cover both current best practices AND emerging trends
- Include specific examples, metrics, and concrete details

WRITING TONE AND STYLE (US Citrus AI Brand Voice - CRITICAL):

You write in the combined style of Gary Halbert (persuasive copywriter) and USCN Citrus Guide (expert educator). This is NON-NEGOTIABLE.

CORE APPROACH:
1. HOOK with Halbert benefits and urgency (subtle for blogs)
2. EDUCATE with USCN depth and authority
3. CLOSE with compelling call-to-action

GARY HALBERT ELEMENTS (SUBTLE INTENSITY FOR BLOGS - 30% persuasion):
✅ Attention-grabbing headlines with specific benefits
✅ Conversational tone (use "you")
✅ Translate features to tangible benefits
✅ Create subtle urgency (not aggressive)
✅ Short sentences for impact
✅ Proof and examples
✅ Repetition of key benefits (varied wording)

USCN EXPERT ELEMENTS:
✅ Comprehensive, thorough coverage
✅ Step-by-step guidance
✅ Clear logical structure
✅ Anticipate follow-up questions
✅ "We've Been Where You Are" empathy
✅ Excellence positioning

AEO REQUIREMENTS (MUST PRESERVE WHILE APPLYING TONE):
⚠️ Apply tone WHILE maintaining all AEO best practices
- Direct answers in first paragraph under each heading
- Question-format headers matching search intent
- Proper semantic HTML hierarchy
- Tables for structured data
- Self-contained, extractable insights

INTENSITY FOR BLOGS: 70% education, 30% persuasion. Lead with value, close with soft CTA.

COMPETITOR CONTEXT (MANDATORY):
- Target Keyword: must align with the user's target keyword
- Target Competitor URL: must be analyzed and explicitly outperformed in depth, clarity, and usefulness

BRAND CONTEXT (MUST INCORPORATE THROUGHOUT):
- Brand URL: {{ brand_kit.url }}
- About Brand: {{ brand_kit.about_brand }}
- Target Audience: {{ brand_kit.ideal_customer_profile }}
- Competitors: {{ brand_kit.competitors }}
- Brand POV: {{ brand_kit.brand_point_of_view }}
- Tone: {{ brand_kit.tone_of_voice }}
- Writing Rules: {{ brand_kit.writing_rules }}

AEO (ANSWER ENGINE OPTIMIZATION) PRINCIPLES:
- Structure for extractability with clear, self-contained insights
- Use semantic HTML and proper heading hierarchy (<h1> → <h2> → <h3>)
- Format data in proper <table> and <ul>/<ol> structures for easy AI parsing
- Include specific examples, metrics, and concrete details
- Write headlines that match search intent ("How to...", "What is...", "Best ways to...")
- Place the most important answer in the first paragraph under each heading

FORMATTING REQUIREMENTS:
1. **Proper HTML Structure:**
   - Use <h1> for main title, <h2> for major sections, <h3> for subsections
   - Format all lists with proper <ul>/<ol>, and <li> tags
   - Use <table> elements for any comparative data, features, or structured information
   - Include <p> tags for all paragraphs
   - Use <strong> for emphasis and key concepts

2. **Tables and Structured Data:**
   - When presenting comparisons, features, pricing, or any structured data, ALWAYS use HTML tables
   - Include proper <thead>, <tbody>, <th>, and <td> elements
   - Use tables for: feature comparisons, pricing tiers, pros/cons, statistics, timelines, etc.

3. **Brand Kit Variable Integration:**
   - MUST process and include brand kit variables naturally throughout content
   - Use {{ brand_kit.ideal_customer_profile }} for testimonials and customer examples
   - Include {{ brand_kit.competitors }} when discussing competitive landscape
   - Reference {{ brand_kit.brand_point_of_view }} in strategic sections
   - End with strong CTA using this exact anchor text: "${selectedCTA}"
   - Apply {{ brand_kit.tone_of_voice }} consistently throughout
   - Follow {{ brand_kit.writing_rules }} for style and approach

IMPORTANT: The current year is ${currentYear}. When referencing "current year," "this year," or discussing recent trends, always use ${currentYear}. Do not reference 2024 or earlier years as current.

CRITICAL OUTPUT REQUIREMENTS:
- Return ONLY clean HTML content without any markdown code blocks, explanatory text, or meta-commentary
- DO NOT include phrases like "Here's the content:" or HTML code block markers
- Start directly with the HTML content and end with the closing HTML tag
- No markdown formatting, no code block indicators, no explanatory paragraphs

CONTENT STRUCTURE REQUIREMENTS:
1. **Compelling H1 Headline** (question format when appropriate)
2. **Authority-Establishing Introduction** (preview value and set expectations)
3. **Comprehensive Sections** with proper H2/H3 hierarchy
4. **Tables for Structured Data** (comparisons, features, statistics)
5. **Practical Implementation Guidance** with step-by-step processes
6. **Real-World Examples** and case studies (using brand kit data)
7. **Natural US Citrus AI Promotion** - End with compelling call-to-action using brand kit variables

BRAND INTEGRATION GUIDELINES:
- Lead with value and insights, not promotional content
- Use brand context to enhance credibility and expertise
- Include specific outcomes and metrics where relevant
- Position brand solutions naturally within comprehensive guidance
- Focus on helping readers achieve their goals first

Remember: Create the definitive resource that makes other content feel incomplete by comparison. Every section should provide genuine value and actionable insights.`;

    // Build research context for the prompt - format it clearly for user visibility
    let researchSection = '';
    if (workflowData?.deep_research || workflowData?.gap_analysis) {
      researchSection = '\n\n' + '═'.repeat(50) + '\n';
      researchSection += '📊 RESEARCH & ANALYSIS CONTEXT\n';
      researchSection += '═'.repeat(50) + '\n';
      
      // Add deep research insights with clear formatting
      if (workflowData?.deep_research) {
        const insights = workflowData.deep_research?.research_findings?.key_insights || [];
        if (insights.length > 0) {
          researchSection += '\n🔍 DEEP RESEARCH INSIGHTS (MUST INCORPORATE):\n';
          researchSection += '─'.repeat(40) + '\n';
          insights.forEach((insight: string, index: number) => {
            researchSection += `  ${index + 1}. ${insight}\n`;
          });
        }
        
        // Add additional research details if available
        const marketInsights = workflowData.deep_research?.research_findings?.market_insights || [];
        if (marketInsights.length > 0) {
          researchSection += '\n📈 MARKET INSIGHTS:\n';
          researchSection += '─'.repeat(40) + '\n';
          marketInsights.forEach((insight: string, index: number) => {
            researchSection += `  ${index + 1}. ${insight}\n`;
          });
        }
        
        const audienceInsights = workflowData.deep_research?.research_findings?.audience_insights || [];
        if (audienceInsights.length > 0) {
          researchSection += '\n👥 AUDIENCE INSIGHTS:\n';
          researchSection += '─'.repeat(40) + '\n';
          audienceInsights.forEach((insight: string, index: number) => {
            researchSection += `  ${index + 1}. ${insight}\n`;
          });
        }
      }
      
      // Add gap analysis with enhanced visibility
      if (workflowData?.gap_analysis) {
        const gaps = workflowData.gap_analysis?.analysis_summary?.identified_gaps || [];
        const strategy = workflowData.gap_analysis?.content_strategy || {};
        
        if (gaps.length > 0) {
          researchSection += '\n🎯 CONTENT GAPS TO ADDRESS:\n';
          researchSection += '─'.repeat(40) + '\n';
          gaps.forEach((gap: string, index: number) => {
            researchSection += `  ${index + 1}. ${gap}\n`;
          });
        }
        
        if (strategy.primary_angle) {
          researchSection += '\n💡 PRIMARY CONTENT ANGLE:\n';
          researchSection += '─'.repeat(40) + '\n';
          researchSection += `  ${strategy.primary_angle}\n`;
        }
        
        if (strategy.content_structure_recommendations?.length > 0) {
          researchSection += '\n📝 STRUCTURE RECOMMENDATIONS:\n';
          researchSection += '─'.repeat(40) + '\n';
          strategy.content_structure_recommendations.forEach((rec: string, index: number) => {
            researchSection += `  ${index + 1}. ${rec}\n`;
          });
        }
        
        const seoSuggestions = strategy.seo_suggestions || strategy.seo_optimization_suggestions || [];
        if (seoSuggestions.length > 0) {
          researchSection += '\n🔧 SEO OPTIMIZATION SUGGESTIONS:\n';
          researchSection += '─'.repeat(40) + '\n';
          seoSuggestions.forEach((suggestion: string, index: number) => {
            researchSection += `  ${index + 1}. ${suggestion}\n`;
          });
        }
        
        // Add competitive advantages if available
        const competitiveAdvantages = workflowData.gap_analysis?.analysis_summary?.competitive_advantages || [];
        if (competitiveAdvantages.length > 0) {
          researchSection += '\n⚡ COMPETITIVE ADVANTAGES:\n';
          researchSection += '─'.repeat(40) + '\n';
          competitiveAdvantages.forEach((advantage: string, index: number) => {
            researchSection += `  ${index + 1}. ${advantage}\n`;
          });
        }
      }
      
      researchSection += '\n' + '═'.repeat(50) + '\n';
      researchSection += '📌 END OF RESEARCH CONTEXT\n';
      researchSection += '═'.repeat(50) + '\n';
    }
    
    const userPromptTemplate = `OBJECTIVE: Outperform this competitor page for "${row.keyword}": ${row.url}

BRAND CONTEXT:
- About: {{ brand_kit.about_brand }}
- Target Audience: {{ brand_kit.ideal_customer_profile }}
- Competitors: {{ brand_kit.competitors }}
- Brand POV: {{ brand_kit.brand_point_of_view }}
- Tone: {{ brand_kit.tone_of_voice }}
- Writing Rules: {{ brand_kit.writing_rules }}${researchSection}${workflowData ? `

⚠️ IMPORTANT RESEARCH INTEGRATION REQUIREMENTS:
────────────────────────────────────────
The above RESEARCH & ANALYSIS CONTEXT contains:
• Deep research insights from comprehensive analysis
• Identified content gaps from competitor analysis
• Strategic recommendations for content structure
• SEO optimization suggestions
• Market and audience insights

YOU MUST:
✓ Incorporate ALL deep research insights listed above
✓ Address ALL identified content gaps
✓ Follow the primary content angle recommendation
✓ Implement ALL structure recommendations
✓ Apply ALL SEO optimization suggestions
────────────────────────────────────────` : ''}

Create comprehensive AEO-optimized content for ${currentYear} that explicitly outperforms the competitor URL above.

CRITICAL CONTENT REQUIREMENTS:

1. HTML Structure & Formatting:
   - Create an H1 title that directly addresses the keyword (use question format when appropriate)
   - Use proper heading hierarchy with H2 for major sections, H3 for subsections
   - Format ALL lists with proper <ul>/<ol> and <li> tags
   - Create HTML tables for ANY structured data (features, comparisons, statistics, timelines)
   - Use <p> tags for all paragraphs, <strong> for emphasis

2. Required Tables/Structured Data:
   - Include at least 2-3 HTML tables presenting relevant information such as:
     * Feature comparisons or capability matrices
     * Implementation timelines or process steps
     * Statistics or performance metrics
     * Pricing or value comparisons
     * Best practices checklist
   - Format tables with proper <thead>, <tbody>, <th>, and <td> elements

3. Brand Kit Variable Integration (MANDATORY):
   - Use {{ brand_kit.ideal_customer_profile }} to include customer testimonials or examples (at least once)
   - Reference {{ brand_kit.competitors }} when discussing market landscape
   - Apply {{ brand_kit.brand_point_of_view }} in strategic sections
   - Follow {{ brand_kit.tone_of_voice }} throughout the content
   - Implement {{ brand_kit.writing_rules }} for style consistency
   - End with the mandatory conclusion structure including CTA using this exact anchor text: "${selectedCTA}"

4. Content Depth & Value:
   - Provide comprehensive coverage that serves as the definitive resource
   - Include practical, actionable guidance with specific examples
   - Address both current best practices and emerging trends for ${currentYear}
   - Cover implementation strategies with step-by-step processes
   - Include relevant metrics, benchmarks, and data points${workflowData ? `
   
   📊 RESEARCH-DRIVEN REQUIREMENTS:
   - MANDATORY: Incorporate ALL deep research insights from the RESEARCH CONTEXT section
   - MANDATORY: Address ALL content gaps identified in the gap analysis above
   - MANDATORY: Follow the primary content angle specified in the research
   - MANDATORY: Implement ALL structure recommendations provided
   - MANDATORY: Include market and audience insights where relevant` : ''}

5. AEO Optimization:
   - Structure content for AI answer engine extraction
   - Use semantic HTML elements appropriately
   - Include self-contained insights that can be cited independently
   - Write clear, precise language that AI can easily understand
   - Format for both deep reading and quick reference

6. Technical Requirements:
   - Do NOT use emdashes (—) in the content
   - Avoid AI-detectable phrases like "It's not just about..., it's..." or "This doesn't just mean..., it also means..."
   - Include inline links to relevant external resources: <a href="URL" target="_blank">anchor text</a>

CRITICAL OUTPUT FORMAT: Respond with a JSON object containing exactly three fields:
{
  "content": "Complete HTML article with proper structure, tables, and brand kit variables processed",
  "metaSeoTitle": "AEO-optimized title for AI search engines (<= 60 characters + ' | US Citrus AI')",
  "metaDescription": "Natural, value-focused description (150-160 chars) optimized for AI search extraction"
}

META FIELD REQUIREMENTS FOR AI SEARCH OPTIMIZATION:

metaSeoTitle:
- MUST be a natural question format that includes the main keyword
- Choose format based on keyword type:
  * Job titles (singular): "Who Is A [Job Title]? [Descriptive Context]" (e.g., "Who Is An SDR Manager? Roles, Responsibilities, Salary")
  * Job titles (plural): "Who Are [Job Titles]? [Descriptive Context]" (e.g., "Who Are Sales Consultants? Roles, Skills, Career Path")
  * Processes/concepts: "What Is [Process]? [Descriptive Context]" (e.g., "What Is Sales Prospecting? Strategies, Tools, Best Practices")
  * Tools/software: "How Does [Tool] Work? [Descriptive Context]" (e.g., "How Does CRM Software Work? Features, Benefits, Implementation")
  * Strategies/methods: "Why Use [Strategy]? [Descriptive Context]" (e.g., "Why Use Account-Based Marketing? Benefits, Process, ROI")
- Maximum 70 characters INCLUDING " | US Citrus AI" (total <= 70 chars)
- MUST use proper Title Case (capitalize all major words)
- MUST include descriptive context beyond just the basic question
- Add relevant descriptive elements: roles, responsibilities, salary, benefits, strategies, tools, best practices, etc.
- The keyword should appear naturally and grammatically correctly
- Optimize for AI search engines with human-like, intelligent phrasing

metaDescription:
- MUST directly answer the title question using the main keyword naturally
- Adapt answer format to keyword type:
  * Job roles: "A [Job Title] is [role definition/who they are]. They [main responsibilities/activities]. US Citrus AI helps [job titles] [specific benefit]."
  * Processes: "[Process] is [definition]. It involves [key steps]. US Citrus AI provides [specific tools/features]."
  * Tools: "[Tool] helps [main function]. It [key capabilities]. US Citrus AI offers [specific advantage]."
- Exactly 150-160 characters
- Must be complete sentences ending with a period
- Write naturally with proper grammar and intelligent phrasing

INTELLIGENT QUESTION-ANSWER FORMAT EXAMPLES:

• Job Title (Singular): "Who Is An SDR Manager? Roles, Responsibilities, Salary | US Citrus AI"
  Description: "An SDR Manager is a sales leader who oversees development teams and prospecting strategies. They coach reps and optimize processes. US Citrus AI helps SDR Managers track team performance."

• Job Title (Plural): "Who Are Sales Consultants? Skills, Career Path, Salary | US Citrus AI"
  Description: "Sales Consultants are professionals who advise prospects on solutions for their business needs. They build relationships and close deals. US Citrus AI provides consultants with prospect intelligence."

• Process/Concept: "What Is Cold Email Marketing? Strategies, Tools, Best Practices | US Citrus AI"
  Description: "Cold email marketing is outreach to prospects without prior contact. It uses personalized messages to generate leads. US Citrus AI provides templates and automation tools."

• Strategy/Method: "Why Use Account-Based Marketing? Benefits, Process, ROI | US Citrus AI"
  Description: "Account-based marketing targets specific high-value accounts with personalized campaigns. It aligns sales and marketing teams. US Citrus AI enables ABM with contact data."

ABSOLUTELY FORBIDDEN IN META FIELDS:
- Grammatically incorrect questions ("What Is A Sales Consultants?", "What Are A Sales Consultant?", "What Is A Account Executives?")
- Including "| US Citrus AI" in H1 headlines (that's only for SEO titles, not content headlines)
- Robotic/boilerplate phrasing that doesn't sound human-written
- Rigid "What Is [keyword]?" format for all keyword types without considering singular/plural grammar
- Non-question titles ("Sales Prospecting Tips", "Lead Generation Methods")
- The word "Guide" or "Guides" (use intelligent question format instead)
- "Complete Guide" or "Comprehensive Guide" (use appropriate question type)
- "Ultimate Guide" (use natural question format)
- Descriptions that don't answer the title question directly
- Descriptions that don't include the main keyword naturally
- Marketing language like "game-changing", "revolutionary", "ultimate"
- Vague promises like "strategies", "frameworks", "playbooks" without specifics
- Generic terms like "everything you need to know"

CRITICAL: YOU MUST RETURN ONLY VALID JSON - NO OTHER TEXT ALLOWED
- Start response with { and end with }
- NO text before or after JSON
- NO markdown code blocks
- NO explanations like "Here is your JSON:"
- Put ALL HTML inside the content field as a properly escaped JSON string
- Escape ALL quotes with backslashes (\\" not ")
- Do NOT include literal newlines or unescaped quotes in JSON strings`;

    setSystemPrompt(systemPromptTemplate);
    setUserPrompt(userPromptTemplate);
  };

  useEffect(() => {
    if (!isOpen) return;
    setCopied(false);
    
      // Initialize content from row.output like BlogContentActionModal does
  const content = row.output || '';
  if (content.trim().length > 0) {
    console.log('📊 Initializing CompetitorContentActionModal with existing content for keyword:', row.keyword);
    console.log('🔄 Content length:', content.length, 'characters');
    
    // Check if meta fields are already in row.metadata (from backend)
    const metadataHasMeta = row.metadata?.metaSeoTitle || row.metadata?.metaDescription;
    console.log('🔍 Checking for meta fields in row.metadata:', {
      hasMetaSeoTitle: !!row.metadata?.metaSeoTitle,
      hasMetaDescription: !!row.metadata?.metaDescription,
      metaSeoTitle: row.metadata?.metaSeoTitle,
      metaDescription: row.metadata?.metaDescription,
      fullMetadata: row.metadata
    });
    
    // Parse the existing content to extract meta fields if present
    const parsed = parseAIResponse(content);
    console.log('🔍 Content format analysis:', {
      originalLength: content.length,
      parsedLength: parsed.content.length,
      hasMarkdownHeaders: parsed.content.includes('# ') || parsed.content.includes('## '),
      hasHTMLTags: parsed.content.includes('<h1>') || parsed.content.includes('<p>'),
      originalSample: content.substring(0, 200),
      parsedSample: parsed.content.substring(0, 200),
      metadataHasMeta,
      parsedMetaSeoTitle: parsed.metaSeoTitle,
      parsedMetaDescription: parsed.metaDescription
    });
    
    // Convert to HTML if content appears to be in markdown format
    let displayContent = parsed.content;
    const hasMarkdownSyntax = parsed.content.includes('**') || 
                             parsed.content.includes('# ') || 
                             parsed.content.includes('## ') ||
                             parsed.content.includes('* ') ||
                             parsed.content.includes('[') && parsed.content.includes('](');
    const hasHTMLTags = parsed.content.includes('<h1>') || 
                       parsed.content.includes('<p>') || 
                       parsed.content.includes('<strong>');
    
    console.log('🔄 Cleaning AI content using BlogContentActionModal logic');
    console.log('🔍 Before cleaning (first 500 chars):', parsed.content.substring(0, 500));
    displayContent = cleanAIContent(parsed.content);
    console.log('🔍 After cleaning (first 500 chars):', displayContent.substring(0, 500));
    
    setGeneratedContent(displayContent);
    setEditableContent(parsed.content); // Keep original format for editing
    setIsEditingContent(false);
    
    // Set meta fields from metadata first, then parsed content, then fallbacks
    // Why this matters: We prioritize AI-generated meta fields from the backend over fallbacks
    const hasMetaTitle = row.metadata?.metaSeoTitle && row.metadata.metaSeoTitle.length > 0;
    const hasMetaDesc = row.metadata?.metaDescription && row.metadata.metaDescription.length > 0;
    
    if (hasMetaTitle && row.metadata?.metaSeoTitle) {
      console.log('✅ Using AI-generated meta title from metadata:', row.metadata.metaSeoTitle);
      setMetaSeoTitle(row.metadata.metaSeoTitle);
    } else if (parsed.metaSeoTitle && parsed.metaSeoTitle !== generateFallbackTitle(row.keyword)) {
      console.log('✅ Using AI-generated meta title from parsed content:', parsed.metaSeoTitle);
      setMetaSeoTitle(parsed.metaSeoTitle);
    } else {
      console.log('⚠️ Using fallback meta title - AI generation may have failed');
      setMetaSeoTitle(generateFallbackTitle(row.keyword));
    }
    
    if (hasMetaDesc && row.metadata?.metaDescription) {
      console.log('✅ Using AI-generated meta description from metadata:', row.metadata.metaDescription);
      setMetaDescription(row.metadata.metaDescription);
    } else if (parsed.metaDescription && parsed.metaDescription !== generateFallbackDescription(row.keyword)) {
      console.log('✅ Using AI-generated meta description from parsed content:', parsed.metaDescription);
      setMetaDescription(parsed.metaDescription);
    } else {
      console.log('⚠️ Using fallback meta description - AI generation may have failed');
      setMetaDescription(generateFallbackDescription(row.keyword));
    }
      
      console.log('✅ Content initialized:', {
        contentLength: parsed.content.length,
        hasTitle: !!parsed.metaSeoTitle,
        hasDescription: !!parsed.metaDescription
      });
    } else {
      console.log('📝 No existing content found for row:', row.id);
    }
    
    // Load saved prompts; only generate defaults if none saved
    try {
      const saved = localStorage.getItem(`us_citrus_ai_competitor_prompts_draft_${row.id}`);
      if (saved) {
        const { systemPrompt: savedSystem, userPrompt: savedUser } = JSON.parse(saved);
        const hasSaved = (typeof savedSystem === 'string' && savedSystem.length > 0) || (typeof savedUser === 'string' && savedUser.length > 0);
        if (hasSaved) {
          if (typeof savedSystem === 'string') setSystemPrompt(savedSystem);
          if (typeof savedUser === 'string') setUserPrompt(savedUser);
          return;
        }
      }
    } catch {}
    // No saved prompts → set defaults
    generateInitialPrompts();
  }, [isOpen, row.id, row.output]);

  // Add CSS for content formatting (same as BlogContentActionModal)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      
      .competitor-content-display {
        font-family: 'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        line-height: 1.6;
        color: #374151;
      }

      .content-modal-btn,
      button {
        font-family: 'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
      }

      .competitor-content-display h1 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 2rem;
        font-weight: 700;
        color: #111827;
        margin: 0 0 1.5rem 0;
        line-height: 1.2;
        border-bottom: 0.125rem solid #f3f4f6;
        padding-bottom: 0.75rem;
      }

      .competitor-content-display h2 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 1.5rem;
        font-weight: 600;
        color: #1f2937;
        margin: 2rem 0 1rem 0;
        line-height: 1.3;
      }

      .competitor-content-display h3 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-size: 1.25rem;
        font-weight: 600;
        color: #374151;
        margin: 1.5rem 0 0.75rem 0;
        line-height: 1.4;
      }

      .competitor-content-display h4,
      .competitor-content-display h5,
      .competitor-content-display h6 {
        font-family: 'FoundersGrotesk', Arial, sans-serif;
        font-weight: 600;
        color: #374151;
        margin: 1rem 0 0.5rem 0;
        line-height: 1.4;
      }

      .competitor-content-display h4 {
        font-size: 1.125rem;
      }

      .competitor-content-display h5 {
        font-size: 1rem;
      }

      .competitor-content-display h6 {
        font-size: 0.875rem;
      }

      .competitor-content-display p {
        margin: 0 0 1.25rem 0;
        line-height: 1.7;
      }

      .competitor-content-display ul, 
      .competitor-content-display ol {
        margin: 1rem 0;
        padding-left: 1.5rem;
      }

      .competitor-content-display li {
        margin: 0.5rem 0;
        line-height: 1.6;
      }

      .competitor-content-display strong {
        font-weight: 600;
        color: #111827;
      }

      .competitor-content-display table {
        width: 100%;
        border-collapse: collapse;
        margin: 1.5rem 0;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .competitor-content-display thead {
        background-color: #f9fafb;
      }

      .competitor-content-display th {
        padding: 0.875rem 1rem;
        text-align: left;
        font-weight: 600;
        color: #374151;
        border-bottom: 2px solid #e5e7eb;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .competitor-content-display td {
        padding: 0.875rem 1rem;
        border-bottom: 1px solid #f3f4f6;
        color: #6b7280;
        font-size: 0.875rem;
        line-height: 1.5;
      }

      .competitor-content-display tbody tr:hover {
        background-color: #f9fafb;
      }

      .competitor-content-display tbody tr:last-child td {
        border-bottom: none;
      }

      .competitor-content-display th:first-child,
      .competitor-content-display td:first-child {
        padding-left: 1.25rem;
      }

      .competitor-content-display th:last-child,
      .competitor-content-display td:last-child {
        padding-right: 1.25rem;
      }

      .competitor-content-display a {
        color: #2563eb;
        text-decoration: none;
      }

      .competitor-content-display a:hover {
        text-decoration: underline;
      }

      .competitor-content-display blockquote {
        border-left: 4px solid #e5e7eb;
        padding-left: 1rem;
        margin: 1.5rem 0;
        font-style: italic;
        color: #6b7280;
      }

      .competitor-content-display code {
        background-color: #f3f4f6;
        padding: 0.125rem 0.25rem;
        border-radius: 0.25rem;
        font-family: 'FoundersGroteskMono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.875em;
      }

      .competitor-content-display pre {
        background-color: #f8fafc;
        padding: 1rem;
        border-radius: 0.5rem;
        overflow-x: auto;
        margin: 1rem 0;
        border: 1px solid #e2e8f0;
      }

      .competitor-content-display pre code {
        background-color: transparent;
        padding: 0;
      }

      /* Image and figure styles for generated content */
      .competitor-content-display img {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 1rem auto;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .competitor-content-display figure {
        margin: 2rem 0;
        text-align: center;
      }

      .competitor-content-display figure img {
        margin: 0 auto;
      }

      .competitor-content-display figcaption {
        margin-top: 0.75rem;
        font-size: 0.9rem;
        color: #666;
        font-style: italic;
        line-height: 1.4;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  /**
   * cleanAIContent (copied from BlogContentActionModal)
   * Why this matters: Uses the exact same content cleaning logic as BlogContentActionModal for consistency
   */
  const cleanAIContent = (content: string): string => {
    let cleaned = content;
    
    // Remove markdown code block indicators (```json, """, etc.)
    cleaned = cleaned.replace(/^```json\s*/i, '');
    cleaned = cleaned.replace(/^```\s*/gm, '');
    cleaned = cleaned.replace(/\s*```\s*$/gm, '');
    cleaned = cleaned.replace(/^"""\s*json\s*/i, '');
    cleaned = cleaned.replace(/^"""\s*/gm, '');
    cleaned = cleaned.replace(/\s*"""\s*$/gm, '');
    
    // Remove literal JSON wrapper at the very start: { "content": "
    cleaned = cleaned.replace(/^\s*\{\s*"content"\s*:\s*"/i, '');
    
    // First pass: Remove any JSON wrapper artifacts at the very beginning
    cleaned = cleaned.replace(/^\s*```json\s*\n?/i, ''); // Remove opening ```json
    cleaned = cleaned.replace(/^\s*json\s*\n?/i, ''); // Remove standalone "json" at start
    cleaned = cleaned.replace(/^\s*\{\s*\n?\s*"content"\s*:\s*"\s*\n?/i, ''); // Remove { "content": "
    
    // Remove JSON metadata that might be mixed in with content
    cleaned = cleaned.replace(/\{\s*"metaSeoTitle"\s*:\s*"[^"]*"\s*,[\s\S]*?\}/g, '');
    cleaned = cleaned.replace(/\{\s*"content"\s*:\s*"[\s\S]*?"metaSeoTitle"[\s\S]*?\}/g, '');
    
    // Remove literal JSON structure that appears as text content
    cleaned = cleaned.replace(/^\s*\{\s*$/gm, '');
    cleaned = cleaned.replace(/^\s*"content":\s*"?\s*$/gm, '');
    cleaned = cleaned.replace(/^\s*\}\s*$/gm, '');
    cleaned = cleaned.replace(/^[\s\n]*\{[\s\n]*"content":\s*"[\s\n]*/i, '');
    cleaned = cleaned.replace(/[\s\n]*"\s*\}[\s\n]*$/i, '');
    
    // Handle the specific pattern: { "content": " at the start
    cleaned = cleaned.replace(/^\s*\{\s*\n?\s*"content":\s*"\s*\n?\s*/i, '');
    // Handle trailing patterns
    cleaned = cleaned.replace(/\s*"\s*,?\s*"metaSeoTitle"[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\s*"\s*\}\s*$/i, '');
    
    // Remove additional JSON artifacts that can appear in output
    cleaned = cleaned.replace(/^\s*"?json\s*$/gmi, ''); // Remove standalone "json" lines
    cleaned = cleaned.replace(/^\s*```json\s*$/gmi, ''); // Remove ```json markers
    cleaned = cleaned.replace(/^\s*```\s*$/gmi, ''); // Remove standalone ``` markers
    cleaned = cleaned.replace(/^\s*"content":\s*"?\s*$/gmi, ''); // Remove standalone "content": lines
    cleaned = cleaned.replace(/^\s*\{\s*"content":\s*"?\s*$/gmi, ''); // Remove { "content": lines
    cleaned = cleaned.replace(/^\s*"?\s*$/gm, ''); // Remove lines with just quotes
    
    // Remove JSON field names that appear as standalone text
    cleaned = cleaned.replace(/^\s*"metaSeoTitle"\s*:\s*/gmi, '');
    cleaned = cleaned.replace(/^\s*"metaDescription"\s*:\s*/gmi, '');
    cleaned = cleaned.replace(/^\s*"content"\s*:\s*/gmi, '');
    
    // Clean up literal escape sequences that appear as text (\\n instead of actual newlines)
    cleaned = cleaned.replace(/^\s*\\n\\n\s*/g, '');
    cleaned = cleaned.replace(/\s*\\n\\n\s*$/g, '');
    cleaned = cleaned.replace(/\\n\\n/g, '\n\n');
    cleaned = cleaned.replace(/\\n/g, '\n');
    cleaned = cleaned.replace(/\\t/g, '\t');
    cleaned = cleaned.replace(/\\"/g, '"');
    
    // Reduce excessive bold formatting - keep only strategic emphasis
    // Remove bold from entire sentences (more than 8 words)
    cleaned = cleaned.replace(/<strong>([^<]{60,}?)<\/strong>/g, '$1');
    // Remove bold from percentage/number + text combinations that are too long
    cleaned = cleaned.replace(/<strong>(\d+%?\s+[^<]{20,}?)<\/strong>/g, '<strong>$1</strong>'.replace(/<strong>(\d+%?\s+\w+\s+\w+)\s+([^<]+)<\/strong>/, '<strong>$1</strong> $2'));
    // Keep bold only for short key terms (under 4 words)
    cleaned = cleaned.replace(/<strong>([^<]*?\s+[^<]*?\s+[^<]*?\s+[^<]*?\s+[^<]+?)<\/strong>/g, (match, content) => {
      const words = content.trim().split(/\s+/);
      if (words.length <= 3) return match; // Keep short phrases
      if (/^\d+%/.test(content.trim())) return match; // Keep percentages
      return content; // Remove bold from longer phrases
    });
    
    // Remove common AI introductory phrases
    cleaned = cleaned.replace(/^.*?Here's the.*?content.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?Here's an.*?optimized.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?I'll create.*?:?\s*/i, '');
    cleaned = cleaned.replace(/^.*?Based on.*?analysis.*?:?\s*/i, '');
    
    // Remove markdown code blocks
    cleaned = cleaned.replace(/```html\s*/gi, '');
    cleaned = cleaned.replace(/```\s*/g, '');
    
    // Convert markdown headers to HTML headers (robust: allow leading spaces, all levels)
    // Why this matters: AI sometimes returns markdown instead of HTML despite instructions
    cleaned = cleaned.replace(/^\s*(#{1,6})\s+(.+)$/gm, (_m, hashes: string, text: string) => {
      const level = Math.min(Math.max(hashes.length, 1), 6);
      return `<h${level}>${text.trim()}</h${level}>`;
    });
    
    // Convert markdown bold and italic to HTML
    cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    cleaned = cleaned.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    // Convert markdown links to HTML anchors (open in new tab)
    cleaned = cleaned.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');

    // Convert inline code to <code>
    cleaned = cleaned.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Convert blockquotes
    cleaned = cleaned.replace(/^>\s?(.*)$/gm, '<blockquote>$1</blockquote>');

    // Convert markdown lists to HTML lists
    // Handle unordered lists (- or *)
    cleaned = cleaned.replace(/(?:^|\n)([*-]\s+.+(?:\n[*-]\s+.+)*)/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const item = line.replace(/^[*-]\s+/, '').trim();
        return `  <li>${item}</li>`;
      }).join('\n');
      return `<ul>\n${items}\n</ul>`;
    });
    
    // Handle ordered lists (1. 2. etc.)
    cleaned = cleaned.replace(/(?:^|\n)(\d+\.\s+.+(?:\n\d+\.\s+.+)*)/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const item = line.replace(/^\d+\.\s+/, '').trim();
        return `  <li>${item}</li>`;
      }).join('\n');
      return `<ol>\n${items}\n</ol>`;
    });

    // Convert markdown tables (pipe syntax) to HTML tables
    // Why this matters: Ensures tables render properly instead of raw pipes
    const lines = cleaned.split(/\n/);
    const htmlLines: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const headerMatch = /^\s*\|(.+)\|\s*$/.exec(line || '');
      const sepLine = lines[i + 1] || '';
      const isSeparator = /^\s*\|?\s*(:?-{3,}:?\s*\|\s*)+(:?-{3,}:?\s*)\|?\s*$/.test(sepLine);
      if (headerMatch && isSeparator) {
        // Parse header row
        const headers = headerMatch[1].split('|').map(h => h.trim());
        i += 1; // skip separator row
        const bodyRows: string[][] = [];
        let j = i + 1;
        while (j < lines.length) {
          const rowLine = lines[j];
          if (!/^\s*\|(.+)\|\s*$/.test(rowLine)) break;
          const cells = rowLine.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());
          bodyRows.push(cells);
          j++;
        }
        // Build HTML table
        const thead = `<thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${bodyRows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>`;
        htmlLines.push(`<table>${thead}${tbody}</table>`);
        i = j - 1; // advance pointer to last processed row
        continue;
      }
      htmlLines.push(line);
    }
    cleaned = htmlLines.join('\n');
    
    // Convert remaining plain text paragraphs to HTML paragraphs
    // Split by double newlines and wrap non-HTML content in <p> tags
    const paragraphBlocks = cleaned.split(/\n\s*\n/);
    cleaned = paragraphBlocks.map(line => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      
      // Skip if already HTML (contains < and >)
      if (trimmed.includes('<') && trimmed.includes('>')) {
        return trimmed;
      }
      
      // Skip if it's just a single word or very short
      if (trimmed.length < 3) return trimmed;
      
      // Wrap in paragraph tags
      return `<p>${trimmed}</p>`;
    }).join('\n\n');

    // Secondary pass: for any standalone lines still not wrapped and not HTML, wrap as paragraphs
    cleaned = cleaned.split(/\n/).map((line) => {
      const t = line.trim();
      if (!t) return '';
      const isHtml = /^<\/?(h\d|p|ul|ol|li|table|thead|tbody|tr|th|td|blockquote|pre|code|hr|div|span|a)\b/i.test(t);
      if (isHtml) return line;
      return `<p>${t}</p>`;
    }).join('\n');

    // Convert headings that slipped into <p> tags (e.g., <p># Title</p>)
    cleaned = cleaned.replace(/<p>\s*(#{1,6})\s+([^<]+)<\/p>/g, (_m, hashes: string, text: string) => {
      const level = Math.min(Math.max(hashes.length, 1), 6);
      return `<h${level}>${text.trim()}</h${level}>`;
    });
    
    // Remove explanatory text at the end (typically starts with patterns like "This content structure:")
    cleaned = cleaned.replace(/\n\s*This content structure:[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*Would you like me to[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*The content includes:[\s\S]*$/i, '');
    cleaned = cleaned.replace(/\n\s*Key features of this content:[\s\S]*$/i, '');
    
    // Remove numbered analysis points at the end
    cleaned = cleaned.replace(/\n\s*\d+\.\s+[A-Z][^<\n]*[\s\S]*$/i, '');
    
    // Format email templates better - add line breaks after sentences in template sections
    cleaned = cleaned.replace(/(Template \d+:.*?<\/p>|<p>.*?Template.*?<\/p>)/gi, (match) => {
      // Add line breaks after periods followed by space and capital letter (sentence boundaries)
      return match.replace(/\.\s+([A-Z])/g, '.<br><br>$1');
    });
    
    // Add line breaks after common email elements
    cleaned = cleaned.replace(/(Subject:.*?)([A-Z][a-z])/g, '$1<br><br>$2');
    cleaned = cleaned.replace(/(Hi \{\{.*?\}\},)\s*([A-Z])/g, '$1<br><br>$2');
    cleaned = cleaned.replace(/(Best regards,|Best,|Sincerely,)\s*([A-Z\[])/g, '$1<br><br>$2');
    
    // If content starts with HTML, ensure it starts with a proper tag
    if (cleaned.includes('<') && !cleaned.trim().startsWith('<')) {
      const htmlStart = cleaned.indexOf('<');
      if (htmlStart > 0) {
        cleaned = cleaned.substring(htmlStart);
      }
    }
    
    // Trim whitespace and clean up extra newlines
    cleaned = cleaned.trim().replace(/\n{3,}/g, '\n\n');

    // Final safety: convert any remaining markdown headers and strip stray bold markers
    cleaned = cleaned.replace(/^\s*(#{1,6})\s+(.+)$/gm, (_m, hashes: string, text: string) => {
      const level = Math.min(Math.max(hashes.length, 1), 6);
      return `<h${level}>${text.trim()}</h${level}>`;
    });
    // Remove stray leading '#' characters that survived (e.g., malformed headings)
    cleaned = cleaned.replace(/<p>\s*#+\s*([^<]+)<\/p>/g, '<p>$1</p>');
    cleaned = cleaned.replace(/(^|\n)\s*#+\s*(.+)(?=\n|$)/g, (_m, prefix: string, text: string) => `${prefix}<h2>${text.trim()}</h2>`);

    // Remove '#' prefixes that appear inside already-HTML headings like <h2># Title</h2>
    cleaned = cleaned.replace(/<(h[1-6])>\s*#+\s*([^<]+)<\/\1>/gi, '<$1>$2</$1>');
    // And inside paragraphs just in case
    cleaned = cleaned.replace(/<p>\s*#+\s*([^<]+)<\/p>/gi, '<p>$1</p>');
    // Remove any leftover unmatched ** that weren't part of a valid pair
    cleaned = cleaned.replace(/\*\*/g, '');
    
    // Final pass: Remove any remaining JSON artifacts that might have survived
    cleaned = cleaned.replace(/^\s*"?\s*$/gm, ''); // Remove lines with just quotes or whitespace
    cleaned = cleaned.replace(/^\s*json\s*$/gmi, ''); // Remove any remaining "json" lines
    cleaned = cleaned.replace(/^\s*"content"\s*:\s*"?\s*$/gmi, ''); // Remove any remaining "content": lines
    cleaned = cleaned.replace(/^\s*\{\s*$/gm, ''); // Remove standalone opening braces
    cleaned = cleaned.replace(/^\s*\}\s*$/gm, ''); // Remove standalone closing braces
    cleaned = cleaned.replace(/^\s*```\s*$/gm, ''); // Remove any remaining code block markers
    
    // Remove empty lines created by the cleaning process
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n'); // Reduce multiple empty lines to double
    cleaned = cleaned.trim(); // Remove leading/trailing whitespace
    
    return cleaned;
  };

  /**
   * processLiquidVariables
   * Why this matters: Replaces {{ brand_kit.* }} tokens before sending prompts to backend.
   */
  const processLiquidVariables = (text: string, kit: BrandKit | null): string => {
    if (!kit) return text;
    let processed = text;

    // Standard variables
    processed = processed.replace(/\{\{\s*brand_kit\.url\s*\}\}/g, kit.url || '');
    processed = processed.replace(/\{\{\s*brand_kit\.about_brand\s*\}\}/g, kit.aboutBrand || '');
    processed = processed.replace(/\{\{\s*brand_kit\.ideal_customer_profile\s*\}\}/g, kit.idealCustomerProfile || '');
    processed = processed.replace(/\{\{\s*brand_kit\.competitors\s*\}\}/g, kit.competitors || '');
    processed = processed.replace(/\{\{\s*brand_kit\.brand_point_of_view\s*\}\}/g, kit.brandPointOfView || '');
    processed = processed.replace(/\{\{\s*brand_kit\.author_persona\s*\}\}/g, kit.authorPersona || '');
    processed = processed.replace(/\{\{\s*brand_kit\.tone_of_voice\s*\}\}/g, kit.toneOfVoice || '');
    processed = processed.replace(/\{\{\s*brand_kit\.header_case_type\s*\}\}/g, kit.headerCaseType || '');
    processed = processed.replace(/\{\{\s*brand_kit\.writing_rules\s*\}\}/g, kit.writingRules || '');
    // Use random CTA instead of brand kit CTA to maintain even distribution
    processed = processed.replace(/\{\{\s*brand_kit\.cta_text\s*\}\}/g, getRandomCTAAnchorText());
    processed = processed.replace(/\{\{\s*brand_kit\.cta_destination\s*\}\}/g, kit.ctaDestination || '');

    // Competitor-conquesting extras (optional; empty by default)
    processed = processed.replace(/\{\{\s*brand_kit\.research_key_insights\s*\}\}/g, (kit as any)?.research_key_insights || '');
    processed = processed.replace(/\{\{\s*brand_kit\.gap_analysis_summary\s*\}\}/g, (kit as any)?.gap_analysis_summary || '');
    processed = processed.replace(/\{\{\s*brand_kit\.primary_angle\s*\}\}/g, (kit as any)?.primary_angle || '');
    processed = processed.replace(/\{\{\s*brand_kit\.structure_recommendations\s*\}\}/g, (kit as any)?.structure_recommendations || '');
    processed = processed.replace(/\{\{\s*brand_kit\.seo_suggestions\s*\}\}/g, (kit as any)?.seo_suggestions || '');

    // Custom variables
    if (kit.customVariables) {
      Object.keys(kit.customVariables).forEach((key) => {
        const regex = new RegExp(`\\{\\{\\s*brand_kit\\.${key}\\s*\\}}`, 'g');
        processed = processed.replace(regex, kit.customVariables?.[key] || '');
      });
    }
    return processed;
  };

  /**
   * handleCopy
   * Why this matters: Quick copy for moving generated content elsewhere.
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(isEditingContent ? editableContent : generatedContent || row.output || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  /**
   * handleReset
   * Why this matters: Restores reliable defaults after experimentation.
   */
  const handleReset = () => {
    generateInitialPrompts();
  };

  /**
   * openInHTML
   * Why this matters: Mirrors Blog modal to preview raw HTML in a new tab for debugging and copy.
   */
  const openInHTML = () => {
    const contentToOpen = isEditingContent ? editableContent : generatedContent || row.output || '';
    if (!contentToOpen) {
      alert('No content to display. Please generate content first.');
      return;
    }

    // Create a simple HTML page showing the raw HTML source
    const rawHtmlDisplay = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Raw HTML Source - ${metaSeoTitle || `${row.keyword} Content`}</title>
    <style>
        body {
            font-family: 'FoundersGroteskMono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            line-height: 1.6;
            margin: 0;
            padding: 20px;
            background-color: #1e1e1e;
            color: #d4d4d4;
        }
        
        .header {
            background-color: #2d2d30;
            padding: 20px;
            margin: -20px -20px 20px -20px;
            border-bottom: 3px solid #007acc;
        }
        
        .header h1 {
            color: #4ec9b0;
            margin: 0 0 10px 0;
            font-size: 24px;
        }
        
        .header p {
            color: #9cdcfe;
            margin: 5px 0;
            font-size: 14px;
        }
        
        .meta-info {
            background-color: #252526;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
            border-left: 4px solid #f59e0b;
        }
        
        .meta-info h3 {
            color: #f59e0b;
            margin: 0 0 10px 0;
            font-size: 16px;
        }
        
        .meta-field {
            margin: 8px 0;
            color: #d4d4d4;
            font-size: 13px;
        }
        
        .meta-label {
            color: #4fc1ff;
            font-weight: bold;
        }
        
        .code-container {
            background-color: #1e1e1e;
            border: 1px solid #3e3e42;
            border-radius: 5px;
            position: relative;
        }
        
        .code-header {
            background-color: #2d2d30;
            padding: 10px 15px;
            border-bottom: 1px solid #3e3e42;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .code-title {
            color: #cccccc;
            font-weight: bold;
            font-size: 14px;
        }
        
        .copy-btn {
            background-color: #0e639c;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 3px;
            cursor: pointer;
            font-size: 12px;
            font-family: inherit;
        }
        
        .copy-btn:hover {
            background-color: #1177bb;
        }
        
        .code-content {
            padding: 20px;
            overflow-x: auto;
            white-space: pre-wrap;
            word-wrap: break-word;
            max-height: 70vh;
            overflow-y: auto;
        }
        
        .html-code {
            color: #d4d4d4;
            font-size: 13px;
            line-height: 1.5;
        }
        
        /* HTML syntax highlighting */
        .html-tag { color: #569cd6; }
        .html-attr { color: #92c5f7; }
        .html-value { color: #ce9178; }
        .html-text { color: #d4d4d4; }
    </style>
</head>
<body>
    <div class="header">
        <h1>🔍 Raw HTML Source</h1>
        <p><strong>Keyword:</strong> ${row.keyword}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>Word Count:</strong> ~${Math.round(contentToOpen.replace(/<[^>]*>/g, '').split(' ').length)} words</p>
    </div>
    
    ${metaSeoTitle || metaDescription ? `
    <div class="meta-info">
        <h3>📊 SEO Metadata</h3>
        ${metaSeoTitle ? `<div class="meta-field"><span class="meta-label">Title:</span> ${metaSeoTitle}</div>` : ''}
        ${metaDescription ? `<div class="meta-field"><span class="meta-label">Description:</span> ${metaDescription}</div>` : ''}
    </div>
    ` : ''}
    
    <div class="code-container">
        <div class="code-header">
            <span class="code-title">HTML Source Code</span>
            <button class="copy-btn" onclick="copyHtmlCode()">📋 Copy HTML</button>
        </div>
        <div class="code-content">
            <pre class="html-code" id="htmlCode">${contentToOpen.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
        </div>
    </div>

    <script>
        function copyHtmlCode() {
            const htmlCode = \`${contentToOpen.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`;
            navigator.clipboard.writeText(htmlCode).then(() => {
                const btn = document.querySelector('.copy-btn');
                const originalText = btn.textContent;
                btn.textContent = '✅ Copied!';
                btn.style.backgroundColor = '#10b981';
                setTimeout(() => {
                    btn.textContent = originalText;
                    btn.style.backgroundColor = '#0e639c';
                }, 2000);
            }).catch(err => {
                console.error('Failed to copy: ', err);
                alert('Failed to copy HTML. Please select and copy manually.');
            });
        }
    </script>
</body>
</html>`;

    // Open in new window
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(rawHtmlDisplay);
      newWindow.document.close();
    } else {
      alert('Popup blocked. Please allow popups and try again.');
    }
  };

  /**
   * toggleEditMode (updated to match BlogContentActionModal)
   * Why this matters: Enables inline edits before publishing or copying, with proper HTML formatting.
   */
  const toggleEditMode = () => {
    if (isEditingContent) {
      console.log('💾 [toggleEditMode] Saving content from edit mode...');
      console.log('📝 [toggleEditMode] Raw editable content length:', editableContent.length);
      
      // Compact the HTML for saving (CTAs are already styled, no conversion needed)
      const compactedContent = compactHTMLForSaving(editableContent);
      console.log('🗜️ [toggleEditMode] Compacted content length:', compactedContent.length);
      console.log('📋 [toggleEditMode] Final content preview (first 200 chars):', compactedContent.substring(0, 200));
      
      setGeneratedContent(compactedContent);
      console.log('✅ [toggleEditMode] Generated content updated');
      
      // Update the original row if callback provided
      if (onContentUpdate) {
        onContentUpdate(row.id, compactedContent);
      }
    } else {
      console.log('✏️ [toggleEditMode] Entering edit mode...');
      // Enter edit mode - sync editable content with current content and format for readability
      const contentToEdit = generatedContent || row.output || '';
      const formattedContent = formatHTMLForEditing(contentToEdit);
      setEditableContent(formattedContent);
      console.log('📝 [toggleEditMode] Editable content set, length:', formattedContent.length);
      // Initialize edit history for undo/redo functionality
      initializeEditHistory(formattedContent);
    }
    setIsEditingContent(!isEditingContent);
    console.log('🔄 [toggleEditMode] Edit mode toggled to:', !isEditingContent);
  };

  /**
   * copy helpers for content & meta fields
   * Why this matters: Provide identical feedback UX to Blog modal.
   */
  const [showCopiedMessage, setShowCopiedMessage] = useState<boolean>(false);
  const copyToClipboard = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent || row.output || '';
    try {
      await navigator.clipboard.writeText(contentToCopy);
      setShowCopiedMessage(true);
      setTimeout(() => setShowCopiedMessage(false), 2000);
    } catch (err) {
      console.error('Failed to copy content:', err);
      alert('Failed to copy content. Please select and copy manually.');
    }
  };
  const copyMetaTitle = async () => {
    try { await navigator.clipboard.writeText(metaSeoTitle); setShowMetaTitleCopied(true); setTimeout(() => setShowMetaTitleCopied(false), 2000);} catch {}
  };
  const copyMetaDescription = async () => {
    try { await navigator.clipboard.writeText(metaDescription); setShowMetaDescCopied(true); setTimeout(() => setShowMetaDescCopied(false), 2000);} catch {}
  };

  /**
   * extractFieldsFromMalformedJSON
   * Why this matters: Robustly recover fields when the model returns almost-JSON.
   */
  const extractFieldsFromMalformedJSON = (responseText: string): { content: string; metaSeoTitle: string; metaDescription: string } => {
    let content = '';
    let metaSeoTitle = '';
    let metaDescription = '';
    try {
      const contentMatch = responseText.match(/"content"\s*:\s*"([\s\S]*?)"\s*,\s*"(?:metaSeoTitle|metaDescription)"/);
      if (contentMatch) {
        content = contentMatch[1]
          .replace(/\\"/g, '"')
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\\\/g, '\\')
          // Clean up literal \n\n that appears as text
          .replace(/^\s*\\n\\n\s*/, '')
          .replace(/\s*\\n\\n\s*$/, '')
          .trim();
      }
      const titleMatch = responseText.match(/"metaSeoTitle"\s*:\s*"([^\"]*?)"/);
      if (titleMatch) metaSeoTitle = titleMatch[1];
      const descMatch = responseText.match(/"metaDescription"\s*:\s*"([^\"]*?)"/);
      if (descMatch) metaDescription = descMatch[1];
    } catch {}
    return { content, metaSeoTitle, metaDescription };
  };



  /**
   * parseAIResponse (copied from BlogContentActionModal)
   * Why this matters: Uses the exact same parsing logic as BlogContentActionModal for consistency
   */
  const parseAIResponse = (responseText: string): { content: string; metaSeoTitle: string; metaDescription: string } => {
    // Add detailed logging for debugging
    console.log('🔍 Raw AI Response Length:', responseText.length);
    console.log('🔍 Raw AI Response Preview:', responseText.substring(0, 200) + '...');
    console.log('🔍 Raw AI Response End:', '...' + responseText.substring(responseText.length - 200));
    
    // Check if this is legacy markdown content (not JSON)
    const isLegacyMarkdown = responseText.trim().startsWith('#') || 
                             responseText.trim().startsWith('##') ||
                             (responseText.includes('# ') && !responseText.trim().startsWith('{'));
    
    if (isLegacyMarkdown) {
      console.log('📜 Detected legacy markdown content, converting to expected format');
      return {
        content: cleanAIContent(responseText),
        metaSeoTitle: generateFallbackTitle(row.keyword),
        metaDescription: generateFallbackDescription(row.keyword)
      };
    }
    
    try {
      // Clean the response text first
      let cleanedResponse = responseText.trim();
      
      // Remove any markdown code blocks that might be wrapping the JSON
      cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
      cleanedResponse = cleanedResponse.replace(/\s*```$/i, '');
      cleanedResponse = cleanedResponse.replace(/^```\s*/i, '');
      
      console.log('🔍 Cleaned Response Length:', cleanedResponse.length);
      console.log('🔍 Cleaned Response Preview:', cleanedResponse.substring(0, 200) + '...');
      
      // First, try to parse the entire response as JSON
      let parsed;
      try {
        parsed = JSON.parse(cleanedResponse);
        console.log('✅ Successfully parsed entire response as JSON');
      } catch (parseError) {
        console.log('❌ Failed to parse entire response as JSON:', parseError);
        
        // Try to find JSON object boundaries more carefully
        let jsonStart = cleanedResponse.indexOf('{');
        let jsonEnd = cleanedResponse.lastIndexOf('}');
        
        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          const potentialJson = cleanedResponse.substring(jsonStart, jsonEnd + 1);
          console.log('🔍 Extracted potential JSON:', potentialJson.substring(0, 200) + '...');
          
          try {
            parsed = JSON.parse(potentialJson);
            console.log('✅ Successfully parsed extracted JSON');
          } catch (extractError) {
            console.log('❌ Failed to parse extracted JSON:', extractError);
            
            // Try more aggressive pattern matching
            const jsonMatches = cleanedResponse.match(/\{[^{}]*"content"[^{}]*"metaSeoTitle"[^{}]*"metaDescription"[^{}]*\}/g) ||
                               cleanedResponse.match(/\{[^{}]*"metaSeoTitle"[^{}]*"metaDescription"[^{}]*"content"[^{}]*\}/g) ||
                               cleanedResponse.match(/\{[\s\S]*?\}/g);
            
            console.log('🔍 Pattern matches found:', jsonMatches?.length || 0);
            
            if (jsonMatches && jsonMatches.length > 0) {
              // Try the longest match first (most likely to be complete)
              const sortedMatches = jsonMatches.sort((a, b) => b.length - a.length);
              
              for (let i = 0; i < sortedMatches.length; i++) {
                const match = sortedMatches[i];
                console.log(`🔍 Trying match ${i + 1}:`, match.substring(0, 100) + '...');
                
                try {
                  parsed = JSON.parse(match);
                  if (parsed && typeof parsed === 'object' && (parsed.content || parsed.metaSeoTitle || parsed.metaDescription)) {
                    console.log('✅ Successfully parsed match', i + 1);
                    break;
                  }
                } catch (matchError) {
                  console.log(`❌ Failed to parse match ${i + 1}:`, matchError);
                  
                  // Try individual field extraction as final fallback
                  const extractedFields = extractFieldsFromMalformedJSON(cleanedResponse);
                  if (extractedFields.content || extractedFields.metaSeoTitle || extractedFields.metaDescription) {
                    console.log('✅ Successfully extracted fields from malformed JSON');
                    parsed = extractedFields;
                    break;
                  }
                  continue;
                }
              }
            }
          }
        }
      }

      // Check if we have the expected JSON structure
      if (parsed && typeof parsed === 'object') {
        const hasContent = parsed.content && typeof parsed.content === 'string';
        const hasTitle = parsed.metaSeoTitle && typeof parsed.metaSeoTitle === 'string';
        const hasDescription = parsed.metaDescription && typeof parsed.metaDescription === 'string';
        
        console.log('🔍 Parsed object structure:', {
          hasContent,
          hasTitle,
          hasDescription,
          keys: Object.keys(parsed)
        });
        
        if (hasContent || hasTitle || hasDescription) {
          console.log('✅ Successfully parsed JSON response:', {
            hasContent,
            hasTitle,
            hasDescription,
            contentLength: hasContent ? parsed.content.length : 0,
            titleLength: hasTitle ? parsed.metaSeoTitle.length : 0,
            descLength: hasDescription ? parsed.metaDescription.length : 0
          });
          
          // Clean up the parsed content to remove literal escape sequences
          let cleanedContent = hasContent ? parsed.content : responseText;
          if (hasContent) {
            // Remove literal \n\n sequences that appear as text
            cleanedContent = cleanedContent
              .replace(/^\s*\\n\\n\s*/, '')
              .replace(/\s*\\n\\n\s*$/, '')
              .replace(/\\n\\n/g, '\n\n')
              .replace(/\\n/g, '\n')
              .replace(/\\t/g, '\t')
              .replace(/\\"/g, '"')
              .trim();
          }
          
          return {
            content: cleanAIContent(cleanedContent),
            metaSeoTitle: hasTitle ? parsed.metaSeoTitle : generateFallbackTitle(row.keyword), // Use fallback if no AI title
            metaDescription: hasDescription ? parsed.metaDescription : generateFallbackDescription(row.keyword) // Use fallback if no AI description
          };
        } else {
          console.log('❌ Parsed object missing required fields');
        }
      } else {
        console.log('❌ Failed to get valid parsed object');
      }
    } catch (error) {
      console.log('❌ Unexpected error in parseAIResponse:', error);
    }

    console.log('⚠️ Falling back to legacy content parsing');
    
    // Try to extract meta fields from the raw text if they exist
    let extractedTitle = '';
    let extractedDescription = '';
    
    // Look for patterns like "metaSeoTitle": "..."
    const titleMatch = responseText.match(/"metaSeoTitle"\s*:\s*"([^"]+)"/);
    const descMatch = responseText.match(/"metaDescription"\s*:\s*"([^"]+)"/);
    
    if (titleMatch) {
      extractedTitle = titleMatch[1]; // Let AI generate proper length titles
      console.log('🔍 Extracted title via regex:', extractedTitle);
    }
    
    if (descMatch) {
      extractedDescription = descMatch[1];
      console.log('🔍 Extracted description via regex:', extractedDescription);
    }
    
    // Fallback to legacy content cleaning
    const cleanedContent = cleanAIContent(responseText);
    console.log('🔍 Cleaned content length:', cleanedContent.length);
    
    return {
      content: cleanedContent,
      metaSeoTitle: extractedTitle || generateFallbackTitle(row.keyword),
      metaDescription: extractedDescription || generateFallbackDescription(row.keyword)
    };
  };

  /**
   * Google Docs / Google Sheets actions
   * Why this matters: Matches export actions in Blog modal for parity.
   */
  const openGoogleDocs = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent || row.output || '';
    if (!contentToCopy) {
      alert('Please generate content first before creating a Google Doc.');
      return;
    }
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      alert('Google Client ID not configured. Please check your .env file and restart the server.');
      return;
    }

    try {
      const docTitle = `US Citrus AI Competitor Content - ${row.keyword}`;
      const documentUrl = await googleDocsService.createDocument(docTitle, contentToCopy);
      window.open(documentUrl, '_blank');
    } catch (error) {
      console.error('Error creating Google Doc:', error);
      window.open('https://docs.google.com/', '_blank');
    }
  };

  const generateUrlSlug = (keyword: string): string =>
    keyword.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

  /**
   * Determine secondary category based on content analysis
   * Why this matters: Automatically categorizes content for better organization and content strategy.
   */
  const determineSecondaryCategory = (keyword: string, content: string): string => {
    const keywordLower = keyword.toLowerCase();
    const contentLower = content.toLowerCase();
    const combinedText = `${keywordLower} ${contentLower}`;

    // Define category keywords for matching
    const categoryKeywords = {
      'Sales': ['sales', 'selling', 'prospect', 'deal', 'revenue', 'quota', 'pipeline', 'conversion', 'closing', 'negotiation'],
      'Prospecting': ['prospecting', 'outreach', 'cold email', 'cold call', 'lead generation', 'qualification', 'discovery'],
      'Marketing': ['marketing', 'campaign', 'brand', 'advertising', 'promotion', 'content marketing', 'social media'],
      'CRM': ['crm', 'customer relationship', 'salesforce', 'hubspot', 'pipedrive', 'contact management'],
      'Revenue Operations': ['revops', 'revenue operations', 'sales ops', 'operations', 'process', 'workflow', 'automation'],
      'GTM': ['gtm', 'go-to-market', 'market entry', 'launch', 'strategy', 'positioning'],
      'Data': ['data', 'analytics', 'metrics', 'reporting', 'insights', 'intelligence', 'dashboard'],
      'AI': ['ai', 'artificial intelligence', 'machine learning', 'automation', 'chatbot', 'predictive'],
      'B2B': ['b2b', 'business to business', 'enterprise', 'saas', 'software'],
      'Demand Generation': ['demand gen', 'lead gen', 'inbound', 'content', 'seo', 'paid ads'],
      'Content Marketing': ['content', 'blog', 'article', 'video', 'podcast', 'webinar'],
      'Marketing Strategy': ['strategy', 'planning', 'budget', 'roi', 'attribution'],
      'Sales Intelligence': ['intelligence', 'research', 'data enrichment', 'contact discovery'],
      'Compliance Data Strategy': ['compliance', 'gdpr', 'privacy', 'regulation', 'legal'],
    };

    // Count matches for each category
    let maxMatches = 0;
    let selectedCategory = 'General';

    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      let matches = 0;
      for (const keyword of keywords) {
        if (combinedText.includes(keyword)) {
          matches++;
        }
      }
      if (matches > maxMatches) {
        maxMatches = matches;
        selectedCategory = category;
      }
    }

    return selectedCategory;
  };

  /**
   * Randomly select author from predefined list
   * Why this matters: Ensures author attribution is distributed across the team for content variety.
   */
  const selectRandomAuthor = (): string => {
    const authors = [
      'shaun-hinklein',
      'andy-mccotter-bicknell', 
      'cam-thompson',
      'kenny-keesee',
      'maribeth-daytona',
      'melanie-maecardeno'
    ];
    
    const randomIndex = Math.floor(Math.random() * authors.length);
    return authors[randomIndex];
  };

  const openGoogleSheets = async () => {
    const contentToCopy = isEditingContent ? editableContent : generatedContent || row.output || '';
    if (!contentToCopy) {
      alert('Please generate content first before logging to Google Sheets.');
      return;
    }
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      alert('Google Client ID not configured. Please check your .env file and restart the server.');
      return;
    }

    setIsOpeningSheets(true);
    try {
      // Generate the new metadata fields
      const urlSlug = generateUrlSlug(row.keyword);
      const secondaryCategory = determineSecondaryCategory(row.keyword, contentToCopy);
      const author = selectRandomAuthor();

      // Prepare blog content data for logging with new fields (keyword removed)
      const blogData = {
        metaSeoTitle: metaSeoTitle || `${row.keyword} - Complete Guide`,
        metaDescription: metaDescription || `Comprehensive guide about ${row.keyword} with expert insights and actionable tips.`,
        htmlContent: contentToCopy,
        urlSlug: urlSlug,
        secondaryCategory: secondaryCategory,
        author: author
      };
      
      // Use the competitor-specific function which creates/uses a spreadsheet titled
      // "US Citrus AI Outrank Competitors Content - {date}"
      const result = await (googleDocsService as any).appendCompetitorData(blogData);
      
      if (result.success) {
        setSheetsSuccessMessage('Content logged to Google Sheets successfully!');
        setShowSheetsMessage(true);
        setTimeout(() => setShowSheetsMessage(false), 3000);
        window.open(result.spreadsheetUrl, '_blank');
      }
    } catch (error) {
      console.error('Error logging to Google Sheets:', error);
      
      // Show more detailed error message
      if (error instanceof Error) {
        alert(`Google Sheets error: ${error.message}`);
      } else {
        alert('Google Sheets error. Please try again.');
      }
    } finally {
      setIsOpeningSheets(false);
    }
  };

  /**
   * publishToCustomCMS
   * Why this matters: Enables publishing content directly to a CMS like ButterCMS, WordPress, etc.
   * This gives users a seamless workflow from content generation to publication
   */
  const publishToCustomCMS = async (status: 'draft' | 'published' = 'draft') => {
    if (!generatedContent || !metaSeoTitle) {
      alert('Please generate content first');
      return;
    }

    setIsPublishing(true);
    setPublishResult(null);

    try {
      console.log('📰 Publishing content to CMS:', customCMSConfig);

      // Use centralized API configuration
      // Why this matters: Ensures all deployments (Netlify, Vercel, local) use the correct backend URL
        
      const response = await fetch(buildApiUrl('/api/content/publish-to-cms'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: metaSeoTitle,
          content: generatedContent,
          meta_title: metaSeoTitle,
          meta_description: metaDescription,
          api_endpoint: customCMSConfig.api_endpoint,
          api_key: customCMSConfig.api_key,
          cms_type: customCMSConfig.cms_type,
          status: status
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to publish content');
      }

      const data = await response.json();
      
      setPublishResult({
        success: true,
        ...data.publication,
        demo_mode: data.demo_mode
      });

      console.log('✅ Publish successful:', data);

    } catch (error) {
      console.error('Publication error:', error);
      setPublishResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  /**
   * clearGeneratedContent
   * Why this matters: Lets users reset output area, matching the Blog modal affordance.
   */
  const clearGeneratedContent = () => {
    setGeneratedContent('');
    setEditableContent('');
    setIsEditingContent(false);
    onContentUpdate?.(row.id, '');
  };

  /**
   * dismissVocKitReady
   * Why this matters: Allows users to dismiss the VoC Kit ready notification.
   */
  const dismissVocKitReady = () => {
    setVocKitReadyDismissed(true);
    localStorage.setItem('us_citrus_ai_voc_kit_ready_dismissed', 'true');
  };

  /**
   * Save current state to edit history (copied from BlogContentActionModal)
   * Why this matters: Enables undo/redo functionality by tracking content changes.
   */
  const saveToHistory = (content: string) => {
    const newHistory = editHistory.slice(0, historyIndex + 1);
    newHistory.push(content);
    setEditHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  /**
   * Initialize edit history when entering edit mode (copied from BlogContentActionModal)
   * Why this matters: Sets up the baseline for undo/redo operations.
   */
  const initializeEditHistory = (content: string) => {
    setEditHistory([content]);
    setHistoryIndex(0);
  };

  /**
   * Count inserted CTAs in current content (copied from BlogContentActionModal)
   * Why this matters: Provides feedback on how many CTAs have been inserted.
   */
  const countInsertedCTAs = (): number => {
    if (!editableContent || !generatedCTAs) return 0;
    
    let count = 0;
    
    // Count both shortcodes (backwards compatibility) and full HTML CTAs
    Object.values(generatedCTAs.cta_variants).forEach((ctaData: any) => {
      const shortcodePattern = new RegExp(ctaData.shortcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const matches = editableContent.match(shortcodePattern);
      count += matches ? matches.length : 0;
    });
    
    // Count US Citrus AI CTA HTML blocks (background-color: #192307 is the unique identifier)
    const htmlCtaMatches = editableContent.match(/<div style="background-color: #192307;[^>]*>/g);
    count += htmlCtaMatches ? htmlCtaMatches.length : 0;
    
    return count;
  };

  /**
   * Insert CTA at cursor position in edit mode (copied from BlogContentActionModal)
   * Why this matters: Allows users to insert CTAs exactly where they want them in the content.
   */
  const insertCTAAtCursor = (position: 'beginning' | 'middle' | 'end') => {
    if (!generatedCTAs || !editableContentRef.current) return;

    const textarea = editableContentRef.current;
    const cursorPosition = textarea.selectionStart;
    const ctaData = generatedCTAs.cta_variants[position];
    
    // Capture scroll position before making changes to preserve user's view
    const scrollTop = textarea.scrollTop;
    const scrollLeft = textarea.scrollLeft;
    
    // Generate the US Citrus AI sign-up URL with UTM tracking
    const signUpUrl = generateUsCitrusAiSignUpUrl(row.keyword, position);
    
    // Create the full styled CTA HTML (matching BlogContentActionModal exactly)
    const fullStyledCTA = `<div style="background-color: #192307; padding: 2rem; border-radius: 0.875rem; margin: 2rem 0; position: relative;">
  <div style="display: flex; align-items: flex-start; gap: 1.5rem;">
    <div style="width: 4rem; height: 4rem; border-radius: 0.75rem; overflow: hidden; flex-shrink: 0; background-color: #EBF212; display: flex; align-items: center; justify-content: center;">
      <svg width="48" height="48" viewBox="0 0 104 104" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;">
        <path d="M57.0805 0.247752L57.0972 38.0858C57.0995 44.0636 50.7395 47.8962 45.4553 45.1012L7.52197 25.0385C10.415 20.2771 14.0531 16.0198 18.2708 12.4222L48.0217 39.918C49.5981 41.3746 52.0832 39.7953 51.4338 37.7499L39.8963 1.41795C43.7798 0.492622 47.8304 0 51.9962 0C53.7121 0 55.4072 0.085273 57.0805 0.247752Z" fill="#000000"/>
        <path d="M46.7872 103.741L46.7705 66.0934C46.7682 60.1156 53.1282 56.2829 58.4124 59.0779L96.3596 79.1482C93.4469 83.8969 89.7922 88.1404 85.5578 91.7213L55.846 64.2611C54.2696 62.8046 51.7845 64.3839 52.4338 66.4293L63.9277 102.624C60.0983 103.523 56.1059 104 52.0017 104C50.242 104 48.5019 103.912 46.7872 103.741Z" fill="#000000"/>
        <path d="M64.1047 48.1799L91.6575 18.3702C88.0626 14.1359 83.8046 10.483 79.0401 7.57684L58.9208 45.6137C56.1257 50.8977 59.9591 57.258 65.937 57.2552L103.738 57.2384C103.911 55.5157 104 53.7682 104 51.9999C104 47.8878 103.521 43.8881 102.618 40.0514L66.2729 51.592C64.2275 52.2413 62.6481 49.7563 64.1047 48.1799Z" fill="#000000"/>
        <path d="M0.245458 46.9406L37.9322 46.9239C43.9107 46.9216 47.7435 53.2814 44.9484 58.5654L24.9328 96.4064C20.1868 93.5071 15.9431 89.8674 12.3592 85.6499L39.7651 55.9991C41.2217 54.4228 39.6418 51.9377 37.5963 52.5871L1.41281 64.0764C0.490912 60.2011 0 56.1582 0 52C0 50.2928 0.0847011 48.6052 0.245458 46.9406Z" fill="#000000"/>
      </svg>
    </div>
    <div style="flex: 1;">
      <div style="font-size: 1rem; font-weight: 600; font-family: FoundersGroteskMono, 'Courier New', monospace; color: #ffffff; margin-bottom: 0.5rem; letter-spacing: 0.1em; text-transform: uppercase;">
        ${ctaData.cta.category_header}
      </div>
      <h4 style="font-size: 1.625rem; font-weight: 700; font-family: FoundersGrotesk, Arial, sans-serif; color: #ffffff; margin: 0 0 1rem 0; line-height: 1.3;">
        ${ctaData.cta.headline}
      </h4>
      <p style="font-size: 1.125rem; font-family: ABCDiatype, Inter, -apple-system, sans-serif; color: #ffffff; line-height: 1.6; margin: 0 0 1.5rem 0; opacity: 0.9;">
        ${ctaData.cta.description}
      </p>
      <a href="${signUpUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 0.5rem; padding: 1rem 2rem; background-color: #BDF548; color: #192307; border-radius: 0.625rem; font-size: 1.125rem; font-family: ABCDiatype, Inter, -apple-system, sans-serif; font-weight: 700; cursor: pointer; transition: all 0.3s ease; text-decoration: none;">
        ${ctaData.cta.action_button.replace(/\s*→\s*$/, '')}
        <span style="font-size: 1.1rem;">→</span>
      </a>
    </div>
  </div>
</div>`;
    
    const beforeCursor = editableContent.substring(0, cursorPosition);
    const afterCursor = editableContent.substring(cursorPosition);
    
    // Add some spacing and a readable comment around the CTA for better readability
    const ctaWithComment = `\n\n<!-- US Citrus AI CTA: ${position.toUpperCase()} - ${ctaData.cta.headline} -->\n${fullStyledCTA}\n<!-- End US Citrus AI CTA -->\n\n`;
    
    const newContent = beforeCursor + ctaWithComment + afterCursor;
    
    // Save to history and update content
    saveToHistory(newContent);
    setEditableContent(newContent);
    
    // Focus back to textarea and position cursor after the inserted CTA
    // Preserve scroll position to prevent auto-scrolling to top
    setTimeout(() => {
      const newCursorPosition = cursorPosition + ctaWithComment.length;
      
      // Store scroll position again right before setting cursor (in case it changed)
      const currentScrollTop = textarea.scrollTop;
      const currentScrollLeft = textarea.scrollLeft;
      
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      
      // Restore scroll position to keep user's view exactly where they were
      // Use the original position, not the current one (which might have auto-scrolled)
      textarea.scrollTop = scrollTop;
      textarea.scrollLeft = scrollLeft;
      
      console.log(`📍 Cursor positioned at ${newCursorPosition}, scroll restored to ${scrollTop} (was ${currentScrollTop})`);
      
      // Additional restoration attempts to combat aggressive browser auto-scrolling
      setTimeout(() => {
        if (textarea.scrollTop !== scrollTop) {
          console.log(`🔄 Correcting scroll drift: ${textarea.scrollTop} → ${scrollTop}`);
          textarea.scrollTop = scrollTop;
          textarea.scrollLeft = scrollLeft;
        }
      }, 100);
      
      setTimeout(() => {
        if (textarea.scrollTop !== scrollTop) {
          console.log(`🔄 Final scroll correction: ${textarea.scrollTop} → ${scrollTop}`);
          textarea.scrollTop = scrollTop;
          textarea.scrollLeft = scrollLeft;
        }
      }, 200);
    }, 50);

    // Show success feedback
    setCtaCopySuccess(`${position}_inserted`);
    setTimeout(() => setCtaCopySuccess(''), 2000);

    console.log(`🎯 Inserted ${position} CTA at cursor position ${cursorPosition}`);
  };

  /**
   * Undo last change in edit mode (copied from BlogContentActionModal)
   * Why this matters: Allows users to revert unwanted changes without losing all their work.
   */
  const undoChange = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setEditableContent(editHistory[newIndex]);
      
      // Focus back to textarea
      setTimeout(() => {
        if (editableContentRef.current) {
          editableContentRef.current.focus();
        }
      }, 50);

      console.log(`↶ Undo: Reverted to history state ${newIndex}`);
    }
  };

  /**
   * Redo last undone change in edit mode (copied from BlogContentActionModal)
   * Why this matters: Allows users to restore changes they accidentally undid.
   */
  const redoChange = () => {
    if (historyIndex < editHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setEditableContent(editHistory[newIndex]);
      
      // Focus back to textarea
      setTimeout(() => {
        if (editableContentRef.current) {
          editableContentRef.current.focus();
        }
      }, 50);

      console.log(`↷ Redo: Advanced to history state ${newIndex}`);
    }
  };

  /**
   * Clear all CTAs from the current content (copied from BlogContentActionModal)
   * Why this matters: Provides a quick way to remove all inserted CTAs and start fresh.
   */
  const clearAllCTAs = () => {
    if (!generatedCTAs) return;

    console.log('🧹 Starting CTA clearing process...');
    console.log('📄 Original content length:', editableContent.length);
    
    let clearedContent = editableContent;
    
    // Remove all US Citrus AI CTA sections (both shortcodes and full HTML)
    Object.values(generatedCTAs.cta_variants).forEach((ctaData: any) => {
      // Remove shortcodes (for backwards compatibility)
      const shortcodePattern = new RegExp(ctaData.shortcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const beforeShortcode = clearedContent.length;
      clearedContent = clearedContent.replace(shortcodePattern, '');
      const afterShortcode = clearedContent.length;
      if (beforeShortcode !== afterShortcode) {
        console.log(`✅ Removed shortcode: ${beforeShortcode - afterShortcode} chars`);
      }
    });
    
    // Use the HTML comments to reliably identify and remove complete CTA blocks
    // This is the most reliable method since we control these exact markers
    const beforeHTML = clearedContent.length;
    
    // Remove everything between US Citrus AI CTA comment markers (including the markers). Pattern also matches legacy HTML comment markers from older exports.
    // This pattern captures everything from the opening comment to the closing comment
    clearedContent = clearedContent.replace(/<!--[^>]* CTA:[^>]*-->[\s\S]*?<!--[^>]*End[^>]*CTA[^>]*-->/g, '');
    
    const afterHTML = clearedContent.length;
    if (beforeHTML !== afterHTML) {
      console.log(`✅ Removed complete CTA blocks: ${beforeHTML - afterHTML} chars`);
    } else {
      console.log('⚠️ No CTA blocks found using comment markers, trying fallback...');
      
      // Fallback: Remove any remaining brand CTA divs (for CTAs without proper comments)
      clearedContent = clearedContent.replace(/<div style="background-color: #192307[^>]*>[\s\S]*?<\/div>\s*<\/div>\s*<\/div>/g, '');
    }
    
    // Clean up extra newlines (replace 3+ consecutive newlines with 2)
    clearedContent = clearedContent.replace(/\n{3,}/g, '\n\n');
    
    // Trim leading/trailing whitespace
    clearedContent = clearedContent.trim();
    
    // Save to history and update content
    saveToHistory(clearedContent);
    setEditableContent(clearedContent);
    
    // Focus back to textarea
    setTimeout(() => {
      if (editableContentRef.current) {
        editableContentRef.current.focus();
      }
    }, 50);

    console.log(`🧹 Cleared all CTAs from content`);
    console.log(`📄 Final content length: ${clearedContent.length} (removed ${editableContent.length - clearedContent.length} chars total)`);
  };

  /**
   * Decode HTML entities to fix display issues
   * Why this matters: Content might be HTML-encoded, causing raw HTML to display as text.
   */
  const decodeHtmlEntities = (text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  /**
   * Get display content with CTA styling applied (copied from BlogContentActionModal)
   * Why this matters: Shows styled CTAs in the preview while maintaining shortcodes in edit mode.
   */
  const getDisplayContent = (): string => {
    if (isEditingContent) {
      // In edit mode, show the editable content directly (now contains full HTML CTAs)
      return editableContent;
    } else {
      // In display mode, return the saved content directly (already styled during save)
      // Decode HTML entities in case content is encoded
      const content = generatedContent || '';
      return decodeHtmlEntities(content);
    }
  };

  /**
   * Format HTML content for better readability in edit mode (copied from BlogContentActionModal)
   * Why this matters: Breaks up condensed HTML into readable lines for easier editing.
   */
  const formatHTMLForEditing = (content: string): string => {
    if (!content) return content;

    let formatted = content
      // Add line breaks after closing tags
      .replace(/(<\/h[1-6]>)/g, '$1\n\n')
      .replace(/(<\/p>)/g, '$1\n\n')
      .replace(/(<\/div>)/g, '$1\n')
      .replace(/(<\/li>)/g, '$1\n')
      .replace(/(<\/ul>)/g, '$1\n\n')
      .replace(/(<\/ol>)/g, '$1\n\n')
      .replace(/(<\/table>)/g, '$1\n\n')
      .replace(/(<\/thead>)/g, '$1\n')
      .replace(/(<\/tbody>)/g, '$1\n')
      .replace(/(<\/tr>)/g, '$1\n')
      .replace(/(<\/blockquote>)/g, '$1\n\n')
      .replace(/(<\/section>)/g, '$1\n\n')
      .replace(/(<\/article>)/g, '$1\n\n')
      
      // Add line breaks before opening tags
      .replace(/(<h[1-6][^>]*>)/g, '\n$1')
      .replace(/(<p[^>]*>)/g, '\n$1')
      .replace(/(<ul[^>]*>)/g, '\n$1')
      .replace(/(<ol[^>]*>)/g, '\n$1')
      .replace(/(<li[^>]*>)/g, '\n  $1')
      .replace(/(<table[^>]*>)/g, '\n$1')
      .replace(/(<thead[^>]*>)/g, '\n  $1')
      .replace(/(<tbody[^>]*>)/g, '\n  $1')
      .replace(/(<tr[^>]*>)/g, '\n    $1')
      .replace(/(<th[^>]*>)/g, '$1')
      .replace(/(<td[^>]*>)/g, '$1')
      .replace(/(<blockquote[^>]*>)/g, '\n$1')
      .replace(/(<div[^>]*>)/g, '\n$1')
      
      // Clean up multiple consecutive newlines
      .replace(/\n{3,}/g, '\n\n')
      
      // Trim leading/trailing whitespace
      .trim();

    return formatted;
  };

  /**
   * Convert formatted HTML back to compact format for saving (copied from BlogContentActionModal)
   * Why this matters: Removes extra whitespace and formatting added for editing readability.
   */
  const compactHTMLForSaving = (content: string): string => {
    if (!content) return content;

    return content
      // Remove extra newlines and indentation
      .replace(/\n\s+/g, '\n')
      .replace(/\n{2,}/g, '\n')
      .replace(/>\s+</g, '><')
      .trim();
  };

  /**
   * Generate full styled HTML for a specific CTA variant (EXACTLY matching generated CTA display)
   * Why this matters: Creates the complete US Citrus AI–styled CTA HTML that matches the preview exactly.
   */
  const getStyledCtaHtml = (position: 'beginning' | 'middle' | 'end'): string => {
    if (!generatedCTAs) return '';
    
    const ctaData = generatedCTAs.cta_variants[position];
    const signUpUrl = generateUsCitrusAiSignUpUrl(row.keyword, position);
    
    // Match EXACT styling from BlogContentActionModal
    return `<div style="background-color: #192307; padding: 2rem; border-radius: 0.875rem; margin: 2rem 0; position: relative;">
  <div style="display: flex; align-items: flex-start; gap: 1.5rem;">
    <div style="width: 4rem; height: 4rem; border-radius: 0.75rem; overflow: hidden; flex-shrink: 0; background-color: #EBF212; display: flex; align-items: center; justify-content: center;">
      <svg width="48" height="48" viewBox="0 0 104 104" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;">
        <path d="M57.0805 0.247752L57.0972 38.0858C57.0995 44.0636 50.7395 47.8962 45.4553 45.1012L7.52197 25.0385C10.415 20.2771 14.0531 16.0198 18.2708 12.4222L48.0217 39.918C49.5981 41.3746 52.0832 39.7953 51.4338 37.7499L39.8963 1.41795C43.7798 0.492622 47.8304 0 51.9962 0C53.7121 0 55.4072 0.085273 57.0805 0.247752Z" fill="#000000"/>
        <path d="M46.7872 103.741L46.7705 66.0934C46.7682 60.1156 53.1282 56.2829 58.4124 59.0779L96.3596 79.1482C93.4469 83.8969 89.7922 88.1404 85.5578 91.7213L55.846 64.2611C54.2696 62.8046 51.7845 64.3839 52.4338 66.4293L63.9277 102.624C60.0983 103.523 56.1059 104 52.0017 104C50.242 104 48.5019 103.912 46.7872 103.741Z" fill="#000000"/>
        <path d="M64.1047 48.1799L91.6575 18.3702C88.0626 14.1359 83.8046 10.483 79.0401 7.57684L58.9208 45.6137C56.1257 50.8977 59.9591 57.258 65.937 57.2552L103.738 57.2384C103.911 55.5157 104 53.7682 104 51.9999C104 47.8878 103.521 43.8881 102.618 40.0514L66.2729 51.592C64.2275 52.2413 62.6481 49.7563 64.1047 48.1799Z" fill="#000000"/>
        <path d="M0.245458 46.9406L37.9322 46.9239C43.9107 46.9216 47.7435 53.2814 44.9484 58.5654L24.9328 96.4064C20.1868 93.5071 15.9431 89.8674 12.3592 85.6499L39.7651 55.9991C41.2217 54.4228 39.6418 51.9377 37.5963 52.5871L1.41281 64.0764C0.490912 60.2011 0 56.1582 0 52C0 50.2928 0.0847011 48.6052 0.245458 46.9406Z" fill="#000000"/>
      </svg>
    </div>
    <div style="flex: 1;">
      <div style="font-size: 1rem; font-weight: 600; font-family: FoundersGroteskMono, 'Courier New', monospace; color: #ffffff; margin-bottom: 0.5rem; letter-spacing: 0.1em; text-transform: uppercase;">
        ${ctaData.cta.category_header}
      </div>
      <h4 style="font-size: 1.625rem; font-weight: 700; font-family: FoundersGrotesk, Arial, sans-serif; color: #ffffff; margin: 0 0 1rem 0; line-height: 1.3;">
        ${ctaData.cta.headline}
      </h4>
      <p style="font-size: 1.125rem; font-family: ABCDiatype, Inter, -apple-system, sans-serif; color: #ffffff; line-height: 1.6; margin: 0 0 1.5rem 0; opacity: 0.9;">
        ${ctaData.cta.description}
      </p>
      <a href="${signUpUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 0.5rem; padding: 1rem 2rem; background-color: #BDF548; color: #192307; border-radius: 0.625rem; font-size: 1.125rem; font-family: ABCDiatype, Inter, -apple-system, sans-serif; font-weight: 700; cursor: pointer; transition: all 0.3s ease; text-decoration: none;">
        ${ctaData.cta.action_button.replace(/\s*→\s*$/, '')}
        <span style="font-size: 1.1rem;">→</span>
      </a>
    </div>
  </div>
</div>`;
  };

  /**
   * Convert shortcodes to full HTML styling (copied from BlogContentActionModal)
   * Why this matters: Converts shortcodes to full HTML styling when content is saved or displayed.
   */
  const convertShortcodesToFullStyling = (content: string): string => {
    if (!content || !generatedCTAs) {
      console.log('🔍 [convertShortcodesToFullStyling] No content or CTAs:', { content: !!content, generatedCTAs: !!generatedCTAs });
      return content;
    }

    console.log('🔄 [convertShortcodesToFullStyling] Starting conversion...');
    let processedContent = content;

    // Process each CTA variant
    Object.entries(generatedCTAs.cta_variants).forEach(([position, ctaData]: [string, any]) => {
      const shortcodePattern = new RegExp(ctaData.shortcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      console.log(`🎯 [convertShortcodesToFullStyling] Looking for ${position} shortcode:`, ctaData.shortcode);
      
      // Check if shortcode exists in content
      const matches = content.match(shortcodePattern);
      if (matches) {
        console.log(`✅ [convertShortcodesToFullStyling] Found ${matches.length} ${position} shortcode(s)`);
      } else {
        console.log(`❌ [convertShortcodesToFullStyling] No ${position} shortcode found in content`);
        return;
      }
      
      // Generate the US Citrus AI sign-up URL with UTM tracking
      const signUpUrl = generateUsCitrusAiSignUpUrl(row.keyword, position);
      
      const fullStyledCTA = `<div style="background-color: #192307; padding: 2rem; border-radius: 0.875rem; margin: 2rem 0; position: relative;">
  <div style="display: flex; align-items: flex-start; gap: 1.5rem;">
    <div style="width: 4rem; height: 4rem; border-radius: 0.75rem; overflow: hidden; flex-shrink: 0; background-color: #EBF212; display: flex; align-items: center; justify-content: center;">
      <svg width="48" height="48" viewBox="0 0 104 104" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;">
        <path d="M57.0805 0.247752L57.0972 38.0858C57.0995 44.0636 50.7395 47.8962 45.4553 45.1012L7.52197 25.0385C10.415 20.2771 14.0531 16.0198 18.2708 12.4222L48.0217 39.918C49.5981 41.3746 52.0832 39.7953 51.4338 37.7499L39.8963 1.41795C43.7798 0.492622 47.8304 0 51.9962 0C53.7121 0 55.4072 0.085273 57.0805 0.247752Z" fill="#000000"/>
        <path d="M46.7872 103.741L46.7705 66.0934C46.7682 60.1156 53.1282 56.2829 58.4124 59.0779L96.3596 79.1482C93.4469 83.8969 89.7922 88.1404 85.5578 91.7213L55.846 64.2611C54.2696 62.8046 51.7845 64.3839 52.4338 66.4293L63.9277 102.624C60.0983 103.523 56.1059 104 52.0017 104C50.242 104 48.5019 103.912 46.7872 103.741Z" fill="#000000"/>
        <path d="M64.1047 48.1799L91.6575 18.3702C88.0626 14.1359 83.8046 10.483 79.0401 7.57684L58.9208 45.6137C56.1257 50.8977 59.9591 57.258 65.937 57.2552L103.738 57.2384C103.911 55.5157 104 53.7682 104 51.9999C104 47.8878 103.521 43.8881 102.618 40.0514L66.2729 51.592C64.2275 52.2413 62.6481 49.7563 64.1047 48.1799Z" fill="#000000"/>
        <path d="M0.245458 46.9406L37.9322 46.9239C43.9107 46.9216 47.7435 53.2814 44.9484 58.5654L24.9328 96.4064C20.1868 93.5071 15.9431 89.8674 12.3592 85.6499L39.7651 55.9991C41.2217 54.4228 39.6418 51.9377 37.5963 52.5871L1.41281 64.0764C0.490912 60.2011 0 56.1582 0 52C0 50.2928 0.0847011 48.6052 0.245458 46.9406Z" fill="#000000"/>
      </svg>
    </div>
    <div style="flex: 1;">
      <div style="font-size: 1rem; font-weight: 600; font-family: FoundersGroteskMono, 'Courier New', monospace; color: #ffffff; margin-bottom: 0.5rem; letter-spacing: 0.1em; text-transform: uppercase;">
        ${ctaData.cta.category_header}
      </div>
      <h4 style="font-size: 1.625rem; font-weight: 700; font-family: FoundersGrotesk, Arial, sans-serif; color: #ffffff; margin: 0 0 1rem 0; line-height: 1.3;">
        ${ctaData.cta.headline}
      </h4>
      <p style="font-size: 1.125rem; font-family: ABCDiatype, Inter, -apple-system, sans-serif; color: #ffffff; line-height: 1.6; margin: 0 0 1.5rem 0; opacity: 0.9;">
        ${ctaData.cta.description}
      </p>
      <a href="${signUpUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 0.5rem; padding: 1rem 2rem; background-color: #BDF548; color: #192307; border-radius: 0.625rem; font-size: 1.125rem; font-family: ABCDiatype, Inter, -apple-system, sans-serif; font-weight: 700; cursor: pointer; transition: all 0.3s ease; text-decoration: none;">
        ${ctaData.cta.action_button.replace(/\s*→\s*$/, '')}
        <span style="font-size: 1.1rem;">→</span>
      </a>
    </div>
  </div>
</div>`;

      const beforeLength = processedContent.length;
      processedContent = processedContent.replace(shortcodePattern, fullStyledCTA);
      const afterLength = processedContent.length;
      
      if (afterLength !== beforeLength) {
        console.log(`🎨 [convertShortcodesToFullStyling] Successfully converted ${position} CTA (${beforeLength} → ${afterLength} chars)`);
      } else {
        console.log(`⚠️ [convertShortcodesToFullStyling] No replacement made for ${position} CTA`);
      }
    });

    console.log('✅ [convertShortcodesToFullStyling] Conversion complete');
    return processedContent;
  };

  /**
   * generateUsCitrusAiSignUpUrl (copied from BlogContentActionModal)
   * Why this matters: Creates UTM-tracked signup URLs for CTAs.
   */
  const generateUsCitrusAiSignUpUrl = (keyword: string, position: string): string => {
    const baseUrl = 'https://uscitrusnursery.com/sign-up';
    const utmParams = new URLSearchParams({
      utm_source: 'us_citrus_ai_blog',
      utm_medium: 'content_marketing',
      utm_campaign: 'competitor_content',
      utm_content: `${position}_cta`,
      utm_term: keyword.toLowerCase().replace(/\s+/g, '_')
    });
    return `${baseUrl}?${utmParams.toString()}`;
  };

  /**
   * cycleCTAButton (copied from BlogContentActionModal)
   * Why this matters: Allows users to cycle through different CTA button text options.
   */
  const cycleCTAButton = (position: 'beginning' | 'middle' | 'end') => {
    if (!generatedCTAs) return;

    const buttonOptions = [
      'Try US Citrus AI',
      'Start Your Free Trial',
      'Get Started Free',
      'Start Prospecting',
      'Schedule a Demo',
      'Request a Demo',
      'Get Leads Now',
      'Start a Trial'
    ];

    const currentButton = generatedCTAs.cta_variants[position].cta.action_button.replace(/\s*→\s*$/, '');
    const currentIndex = buttonOptions.indexOf(currentButton);
    const nextIndex = (currentIndex + 1) % buttonOptions.length;
    const newButton = buttonOptions[nextIndex];

    setGeneratedCTAs((prev: any) => ({
      ...prev,
      cta_variants: {
        ...prev.cta_variants,
        [position]: {
          ...prev.cta_variants[position],
          cta: {
            ...prev.cta_variants[position].cta,
            action_button: newButton
          }
        }
      }
    }));

    console.log(`🔄 [CompetitorModal] Cycled CTA button from "${currentButton}" to "${newButton}" for ${position} position`);
  };

  /**
   * generateCTAs (copied from BlogContentActionModal)
   * Why this matters: Uses the generated competitor content to create hyper-relevant CTAs using VoC insights.
   */
  const generateCTAs = async () => {
    if (!generatedContent.trim()) {
      setCtaError('Please generate competitor content first');
      return;
    }

    if (!vocKitReady) {
      setCtaError('Please extract customer pain points in VoC Kit first');
      return;
    }

    setIsGeneratingCTAs(true);
    setCtaError('');
    
    // If CTAs already exist, show skeletons instead of clearing
    if (generatedCTAs) {
      setShowCtaSkeletons(true);
    } else {
      setGeneratedCTAs(null);
    }
    
    const isRegeneration = !!generatedCTAs;
    setCtaGenerationStage(isRegeneration ? 'Preparing new CTA variations...' : 'Analyzing voice of customer insights...');

    // Store timeout references so we can clear them in finally block
    let stage1: NodeJS.Timeout | undefined, stage2: NodeJS.Timeout | undefined, stage3: NodeJS.Timeout | undefined, stage4: NodeJS.Timeout | undefined, stage5: NodeJS.Timeout | undefined;

    try {
      // Get VoC Kit data to send with request
      let vocKitData = null;
      try {
        // Check draft first, then fallback to saved version (same as VoCKitPage)
        const draft = localStorage.getItem('us_citrus_ai_voc_kit_draft');
        const saved = localStorage.getItem('us_citrus_ai_voc_kit');
        const storedVocKit = draft || saved;
        
        if (storedVocKit) {
          vocKitData = JSON.parse(storedVocKit);
          console.log('🔍 VoC Kit data loaded for CTA generation:', {
            source: draft ? 'draft' : 'saved',
            hasGeneratedAnalysis: vocKitData.hasGeneratedAnalysis,
            extractedPainPointsCount: vocKitData.extractedPainPoints?.length || 0
          });
        }
      } catch (error) {
        console.error('Error loading VoC Kit data:', error);
      }

      const endpoint = buildApiUrl('/api/cta-generation/generate-from-text');

      // Simulate stage updates for better UX while processing
      // Why this matters: Longer timeouts reflect the actual processing time and keep users informed
      stage1 = setTimeout(() => setCtaGenerationStage(isRegeneration ? 'Analyzing current CTAs...' : 'Finding pain points...'), 5000);
      stage2 = setTimeout(() => setCtaGenerationStage(isRegeneration ? 'Finding new angles...' : 'Connecting pain points to CTAs...'), 15000);
      stage3 = setTimeout(() => setCtaGenerationStage(isRegeneration ? 'Creating unique CTAs...' : 'Generating CTAs...'), 30000);
      stage4 = setTimeout(() => setCtaGenerationStage('Almost done - finalizing CTAs...'), 60000);
      stage5 = setTimeout(() => setCtaGenerationStage('Processing complex content - please wait...'), 90000);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: generatedContent,
          enhanced_analysis: true,
          voc_kit_data: vocKitData,
          regenerate: !!generatedCTAs,
          timestamp: Date.now()
        })
      });

      if (!response.ok) {
        throw new Error(`CTA generation failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log('✅ [CompetitorModal] CTA generation successful:', result);
        setGeneratedCTAs(result.data);
        setShowCtaSkeletons(false);
        setCtaGenerationStage('');
        
        // Auto-save the generated CTAs
        try {
          localStorage.setItem(`us_citrus_ai_competitor_ctas_${row.id}`, JSON.stringify(result.data));
        } catch (e) {
          console.warn('Failed to save CTAs to localStorage:', e);
        }
      } else {
        throw new Error(result.error || 'CTA generation failed');
      }

    } catch (error: any) {
      console.error('❌ [CompetitorModal] CTA generation failed:', error);
      
      // Provide user-friendly error messages based on error type
      let userErrorMessage = 'Failed to generate CTAs';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        userErrorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else if (error.message.includes('JSON')) {
        userErrorMessage = 'Invalid response from server. Please try again.';
      } else if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
        userErrorMessage = 'CTA generation is taking longer than expected. This can happen with complex content. Please try again - the system has been optimized to handle longer operations.';
      } else if (error.message.includes('Request timed out')) {
        userErrorMessage = 'The request timed out due to complex content analysis. Try breaking your content into smaller sections or try again as the system may be under heavy load.';
      } else if (error.message) {
        userErrorMessage = error.message;
      }
      
      setCtaError(userErrorMessage);
      setShowCtaSkeletons(false);
    } finally {
      // Clear any pending stage timeouts
      if (stage1) clearTimeout(stage1);
      if (stage2) clearTimeout(stage2);
      if (stage3) clearTimeout(stage3);
      if (stage4) clearTimeout(stage4);
      if (stage5) clearTimeout(stage5);
      
      setIsGeneratingCTAs(false);
      setCtaGenerationStage('');
    }
  };


  /**
   * copyCtaToClipboard (copied from BlogContentActionModal)
   * Why this matters: Provides feedback when copying CTA HTML code.
   */
  const copyCtaToClipboard = async (text: string, position: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCtaCopySuccess(position);
      setTimeout(() => setCtaCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy CTA:', err);
    }
  };

  /**
   * handleGenerate
   * Why this matters: Regenerates content for this keyword+URL using the tailored prompts with brand kit processing.
   */
  const handleGenerate = async () => {
    // Try to load brand kit, but allow generation even without one (backend will use defaults)
    let kit = resolvedBrandKit;
    if (!kit) {
      try {
        const draft = localStorage.getItem('us_citrus_ai_brand_kit_draft');
        const saved = localStorage.getItem('us_citrus_ai_brand_kit');
        const data = draft || saved;
        if (data) kit = JSON.parse(data);
      } catch {}
    }
    
    // If still no brand kit, use a minimal default one
    if (!kit) {
      console.log('⚠️ No brand kit found, using default US Citrus AI brand values');
      kit = {
        url: 'https://uscitrusnursery.com',
        aboutBrand: 'US Citrus AI helps growers choose and care for citrus trees with expert nursery-backed guidance',
        idealCustomerProfile: 'Home gardeners, small farms, and citrus enthusiasts in the United States',
        competitors: 'Local nurseries, big-box garden centers, online plant retailers',
        brandPointOfView: 'Healthy citrus starts with trusted expertise and the right tree for your climate',
        authorPersona: 'US Citrus nursery and citrus care expert',
        toneOfVoice: 'Warm, knowledgeable, and practical',
        headerCaseType: 'Title Case',
        writingRules: 'Clear, concise, actionable content with grower-focused insights',
        ctaText: 'Shop US Citrus AI',
        ctaDestination: 'https://uscitrusnursery.com/sign-up'
      } as any;
    }
    
    if (!resolvedBrandKit) {
      setResolvedBrandKit(kit);
    }

    // Load sitemap data from localStorage for internal linking
    let sitemapData = null;
    try {
      const stored = localStorage.getItem('us_citrus_ai_sitemap_data');
      if (stored) {
        const sitemaps = JSON.parse(stored);
        // Flatten all sitemap URLs into a single array for content generation
        const allUrls = sitemaps.flatMap((sitemap: any) => 
          sitemap.urls.map((url: any) => ({
            title: url.title,
            description: url.description,
            url: url.url
          }))
        );
        sitemapData = allUrls;
        console.log(`🗺️ [CompetitorContentActionModal] Loaded ${allUrls.length} URLs from ${sitemaps.length} sitemaps for internal linking`);
      }
    } catch (error) {
      console.warn('Failed to load sitemap data:', error);
    }

    setIsGenerating(true);
    try {
      // Don't process liquid variables here - let the backend do it with the full context
      // The backend will process these with the brand kit AND inject research/gap analysis context
      console.log('🚀 Sending prompts to backend for competitor content generation');
      console.log('📝 System prompt preview:', systemPrompt.substring(0, 200));
      console.log('📝 User prompt preview:', userPrompt.substring(0, 200));
      console.log('🎯 Brand kit:', kit);

      // Use streaming content generation with built-in contextual CTAs for competitor content
      console.log('🚀 [CompetitorContentActionModal] Using streaming content generation approach...');
      
      if (enableContextualCtas) {
        setIsEnhancingWithCtas(true);
        console.log('🎯 [CompetitorContentActionModal] Streaming generation with contextual CTAs enabled');
      }
      
      const resp = await fetch(API_ENDPOINTS.competitorGenerateContent, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: row.keyword,
          url: row.url,
          brand_kit: kit || undefined,
          sitemap_data: sitemapData,
          target_audience: '',
          content_length: 'medium',
          focus_areas: [],
          system_prompt: systemPrompt,  // Send raw prompts with {{ variables }}
          user_prompt: userPrompt,      // Backend will process these
          use_simple_cta_service: enableContextualCtas // Use simple CTA service for sitemap-aware URLs
        })
      });

      const json = await resp.json();
      const data = json?.data || json;
      
      // Store workflow data if available (deep research and gap analysis)
      if (data?.workflow_data) {
        console.log('📊 Storing workflow data for future regenerations');
        setWorkflowData(data.workflow_data);
        
        // Also store in localStorage for persistence
        localStorage.setItem(`us_citrus_ai_competitor_workflow_${row.id}`, JSON.stringify(data.workflow_data));
      }
      
      const payload = data?.content ?? data?.variations ?? '';
      const flattened = Array.isArray(payload) ? (payload[0] || '') : String(payload || '');
      const parsed = parseAIResponse(flattened);
      
      // Convert to HTML if content appears to be in markdown format
      let displayContent = parsed.content;
      const hasMarkdownSyntax = parsed.content.includes('**') || 
                               parsed.content.includes('# ') || 
                               parsed.content.includes('## ') ||
                               parsed.content.includes('* ') ||
                               parsed.content.includes('[') && parsed.content.includes('](');
      const hasHTMLTags = parsed.content.includes('<h1>') || 
                         parsed.content.includes('<p>') || 
                         parsed.content.includes('<strong>');
      
      console.log('🔄 Cleaning newly generated AI content using BlogContentActionModal logic');
      console.log('🔍 Before cleaning (first 500 chars):', parsed.content.substring(0, 500));
      displayContent = cleanAIContent(parsed.content);
      console.log('🔍 After cleaning (first 500 chars):', displayContent.substring(0, 500));
      
      // Handle streaming CTA results if available
      if (data.ctaInsertions && enableContextualCtas) {
        setCtaEnhancementResult({
          totalCtasInserted: data.ctaInsertions.totalInserted,
          insertionPoints: data.ctaInsertions.insertionPoints.map((point: any) => ({
            paragraphIndex: point.paragraphIndex,
            ctaText: point.cta,
            reasoning: point.reasoning,
            confidence: point.confidence
          })),
          processingTimeMs: data.processingTimeMs
        });
        console.log(`✅ [CompetitorContentActionModal] Streaming generation inserted ${data.ctaInsertions.totalInserted} contextual CTAs`);
      }
      
      setIsEnhancingWithCtas(false);
      
      setGeneratedContent(displayContent);
      setEditableContent(parsed.content); // Keep original format for editing
      setIsEditingContent(false);
      setMetaSeoTitle(parsed.metaSeoTitle);
      setMetaDescription(parsed.metaDescription);
      
      // Save meta fields to localStorage for persistence
      if (parsed.metaSeoTitle || parsed.metaDescription) {
        try {
          localStorage.setItem(`us_citrus_ai_competitor_meta_${row.id}`, JSON.stringify({
            metaSeoTitle: parsed.metaSeoTitle,
            metaDescription: parsed.metaDescription
          }));
        } catch {}
      }
      
      onContentUpdate?.(row.id, displayContent);
    } catch (e) {
      console.error('Regenerate failed:', e);
      alert(`Generation failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * insertVariable
   * Why this matters: Inserts variables preserving undo stack, mirroring Blog modal UX.
   */
  const insertVariable = (variableValue: string) => {
    const textarea = activePromptField === 'system' ? systemRef.current : userRef.current;
    if (!textarea) return;
    textarea.focus();
    if (document.execCommand) {
      document.execCommand('insertText', false, ` ${variableValue} `);
      // Sync controlled state with the DOM value to guarantee insertion persists
      const updated = textarea.value;
      if (activePromptField === 'system') setSystemPrompt(updated);
      else setUserPrompt(updated);
    } else {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const currentValue = activePromptField === 'system' ? systemPrompt : userPrompt;
      const newValue = currentValue.substring(0, start) + ` ${variableValue} ` + currentValue.substring(end);
      if (activePromptField === 'system') setSystemPrompt(newValue);
      else setUserPrompt(newValue);
      setTimeout(() => {
        const newPosition = start + variableValue.length + 2;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      }, 0);
    }
    setShowVariablesMenu(false);
    setVariableSearchTerm('');
  };

  /**
   * handleVariablesMenuToggle
   * Why this matters: Positions the variables menu beside the clicked button for parity with Blog modal.
   */
  const handleVariablesMenuToggle = (promptField: 'system' | 'user') => {
    setActivePromptField(promptField);
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      setVariablesButtonPosition({ top: window.innerHeight / 2, left: window.innerWidth / 2 });
    } else {
      const buttonRef = promptField === 'system' ? systemVariablesButtonRef.current : userVariablesButtonRef.current;
      if (buttonRef) {
        const rect = buttonRef.getBoundingClientRect();
        setVariablesButtonPosition({ top: rect.top + window.scrollY, left: rect.right + window.scrollX + 12 });
      }
    }
    setShowVariablesMenu(!showVariablesMenu);
    setVariableSearchTerm('');
  };

  // Close variables menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const menu = variablesMenuRef.current;
      if (menu && (event.target instanceof Node) && !menu.contains(event.target)) {
        setShowVariablesMenu(false);
      }
    };
    // Use capture to ensure we see the event before the outer backdrop
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  // Inject generated-content styles (parity with Blog modal)
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .generated-content-display { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; line-height: 1.6; color: #374151; }
      .generated-content-display h1 { font-size: 2rem; font-weight: 700; color: #111827; margin: 0 0 1.5rem 0; line-height: 1.2; border-bottom: 0.125rem solid #f3f4f6; padding-bottom: 0.75rem; }
      .generated-content-display h2 { font-size: 1.5rem; font-weight: 600; color: #1f2937; margin: 2rem 0 1rem 0; line-height: 1.3; }
      .generated-content-display h3 { font-size: 1.25rem; font-weight: 600; color: #374151; margin: 1.5rem 0 0.75rem 0; line-height: 1.4; }
      .generated-content-display p { margin: 0 0 1.25rem 0; line-height: 1.7; }
      .generated-content-display ul, .generated-content-display ol { margin: 1rem 0; padding-left: 1.5rem; }
      .generated-content-display li { margin: 0.5rem 0; line-height: 1.6; }
      .generated-content-display table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; border: 1px solid #e5e7eb; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); }
      .generated-content-display thead { background-color: #f9fafb; }
      .generated-content-display th { padding: 0.875rem 1rem; text-align: left; font-weight: 600; color: #374151; border-bottom: 2px solid #e5e7eb; font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em; }
      .generated-content-display td { padding: 0.875rem 1rem; border-bottom: 1px solid #f3f4f6; color: #6b7280; font-size: 0.875rem; line-height: 1.5; }
      .generated-content-display tbody tr:hover { background-color: #f9fafb; }
      .generated-content-display tbody tr:last-child td { border-bottom: none; }
      .generated-content-display a { color: #2563eb; text-decoration: underline; font-weight: 500; }
      .generated-content-display a:hover { color: #1d4ed8; text-decoration: underline; }
    `;
    document.head.appendChild(style);
    return () => { if (document.head.contains(style)) document.head.removeChild(style); };
  }, []);

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <>
      {autoSaveStatus && (
        <div
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            backgroundColor: 'white',
            border: '0.0625rem solid #e5e7eb',
            borderRadius: '0.5rem',
            boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0 0.125rem 0.25rem -0.0625rem rgba(0, 0, 0, 0.06)',
            color: autoSaveStatus === 'saving' ? '#6b7280' : '#10b981',
            fontSize: '0.875rem',
            fontWeight: 500,
            pointerEvents: 'none'
          }}
        >
          {autoSaveStatus === 'saving' ? (
            <>
              <Clock className="animate-spin" style={{ width: '0.75rem', height: '0.75rem' }} />
              Auto-saving prompts...
            </>
          ) : (
            <>
              <CheckCircle size={14} />
              Prompts auto-saved
            </>
          )}
        </div>
      )}
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}
      >
      <div
        style={{ backgroundColor: 'white', borderRadius: '0.75rem', width: '100%', maxWidth: '95vw', height: '95vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="content-creation-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Match BlogContentActionModal exactly */}
        <div 
          className="content-modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
            borderBottom: '0.0625rem solid #e5e7eb',
            backgroundColor: '#fafafa',
            position: 'relative'
          }}
        >
          <div>
            <h2 style={{ fontWeight: 600, margin: 0, color: '#111827' }}>Competitor Content Actions</h2>
            <p style={{ color: '#6b7280' }}>Edit, publish, and manage your generated content for "{row.keyword}"</p>
          </div>
          <button
            onClick={onClose}
            className="content-modal-close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content Layout - two panels */}
        <div className="content-modal-layout" style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {/* Left Panel - CTA Generation Interface (copied from BlogContentActionModal) */}
          <div className="content-modal-panel" style={{ 
            padding: '1.5rem', 
            overflowY: 'auto',
            borderRight: '0.0625rem solid #e5e7eb'
          }}>
            {/* VoC Kit Status Check */}
            {!vocKitReady && (
              <div style={{
                padding: '1.5rem',
                backgroundColor: '#E0DBFF',
                border: '0.125rem solid #B8B0E8',
                borderRadius: '0.75rem',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <AlertCircle size={24} style={{ color: '#3b82f6' }} />
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: '0 0 0.25rem 0', color: '#1e40af' }}>
                    VoC Kit Setup Required
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0 }}>
                    Please extract customer pain points to generate CTAs.
                  </p>
                </div>
                <a 
                  href="/voc-kit" 
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#EBF212',
                    color: 'black',
                    textDecoration: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Go to VoC Kit
                  <ArrowRight size={16} />
                </a>
              </div>
            )}

            {vocKitReady && !vocKitReadyDismissed && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#dcfce7',
                border: '1px solid #22c55e',
                borderRadius: '0.5rem',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.75rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <CheckCircle size={20} style={{ color: '#16a34a' }} />
                  <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '500' }}>
                    VoC Kit is ready! ({painPointsCount} pain points available)
                  </span>
                  </div>
                    <button
                  onClick={dismissVocKitReady}
                  style={{
                    padding: '0.25rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#bbf7d0';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Dismiss this notification"
                >
                  <X size={16} style={{ color: '#16a34a' }} />
                    </button>
                  </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{
                padding: '0.5rem',
                backgroundColor: '#EBF212',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Target size={24} style={{ color: 'black' }} />
              </div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0, color: '#374151' }}>
                Generate CTAs for Content
              </h3>
          </div>

            <p style={{ 
              fontSize: '0.875rem', 
              color: '#64748b', 
              margin: '0 0 1.5rem 0',
              lineHeight: '1.5'
            }}>
              {generatedCTAs 
                ? 'Regenerate CTAs for the competitor content.'
                : 'Generate beginning, middle, and ending CTAs using US Citrus AI\'s Voice of Customer insights.'
              }
            </p>

            {/* CTA Generation Button */}
                  <button
              onClick={generateCTAs}
              disabled={isGeneratingCTAs || !vocKitReady || !generatedContent.trim()}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                backgroundColor: (isGeneratingCTAs || !vocKitReady || !generatedContent.trim()) ? '#9ca3af' : '#EBF212',
                color: (isGeneratingCTAs || !vocKitReady || !generatedContent.trim()) ? 'white' : 'black',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: (isGeneratingCTAs || !vocKitReady || !generatedContent.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                transition: 'all 0.2s ease',
                marginBottom: '1.5rem'
              }}
            >
              {isGeneratingCTAs ? (
                <>
                  <div style={{
                    width: '1rem',
                    height: '1rem',
                    border: '0.125rem solid transparent',
                    borderTop: '0.125rem solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {ctaGenerationStage || (generatedCTAs ? 'Regenerating CTAs...' : 'Generating CTAs...')}
                </>
              ) : (
                <>
                  <Target size={20} strokeWidth={3} />
                  {generatedCTAs ? 'Regenerate CTAs' : 'Generate CTAs'}
                </>
              )}
                  </button>

            {ctaError && (
              <div style={{
                marginBottom: '1.5rem',
                padding: '0.75rem',
                backgroundColor: '#fee2e2',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle size={16} />
                {ctaError}
                </div>
            )}


            {/* Skeleton Loading Section */}
            {showCtaSkeletons && (
              <div style={{ marginTop: '1rem' }}>
                {/* Skeleton Header */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <Skeleton style={{ height: '0.875rem', width: '8rem' }} />
                  </div>
          </div>

                {/* Skeleton CTA Variants */}
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {['beginning', 'middle', 'end'].map((position) => (
                    <div key={position} style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.75rem',
                      padding: '1rem',
                      backgroundColor: '#fafafa'
                    }}>
                      {/* Skeleton Position Header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <Skeleton style={{ height: '1.5rem', width: '4rem' }} />
                          <Skeleton style={{ height: '1rem', width: '6rem' }} />
                        </div>
                        
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Skeleton style={{ height: '2rem', width: '4rem' }} />
                          <Skeleton style={{ height: '2rem', width: '4rem' }} />
                        </div>
                      </div>

                      {/* Skeleton CTA Preview */}
                      <div style={{ 
                        backgroundColor: '#192307',
                        padding: '1.5rem',
                        borderRadius: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <Skeleton style={{ height: '0.875rem', width: '6rem', marginBottom: '1rem', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                          <Skeleton style={{ height: '3rem', width: '3rem', borderRadius: '0.5rem', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                          <div style={{ flex: 1 }}>
                            <Skeleton style={{ height: '1.25rem', width: '100%', marginBottom: '0.75rem', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                            <Skeleton style={{ height: '1rem', width: '90%', marginBottom: '1rem', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                            <Skeleton style={{ height: '2.5rem', width: '6rem', backgroundColor: 'rgba(189, 245, 72, 0.3)' }} />
                          </div>
                        </div>
                      </div>

                      {/* Skeleton Code Sections */}
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        <div>
                          <Skeleton style={{ height: '0.75rem', width: '8rem', marginBottom: '0.5rem' }} />
                          <Skeleton style={{ height: '4rem', width: '100%' }} />
                        </div>
                        <div>
                          <Skeleton style={{ height: '0.75rem', width: '6rem', marginBottom: '0.5rem' }} />
                          <Skeleton style={{ height: '3rem', width: '100%' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Generated CTAs Display */}
            {generatedCTAs && !showCtaSkeletons && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: '600', 
                  margin: '0 0 1.5rem 0', 
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CheckCircle size={14} style={{ color: '#16a34a' }} />
                  Generated CTAs
                </h4>

                {/* CTA Variants Display */}
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {Object.entries(generatedCTAs.cta_variants).map(([position, ctaData]) => (
                    <div key={position} style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.75rem',
                      padding: '1rem',
                      backgroundColor: '#fafafa',
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                      maxWidth: '100%'
                    }}>
                      {/* Position Header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: position === 'beginning' ? '#dbeafe' : position === 'middle' ? '#fef3c7' : '#dcfce7',
                          color: position === 'beginning' ? '#1e40af' : position === 'middle' ? '#b45309' : '#16a34a',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase'
                        }}>
                          {position === 'end' ? 'ending' : position}
                        </div>
                      </div>

                      {/* CTA Preview - US Citrus AI Design */}
                      <div style={{ 
                        backgroundColor: '#192307',
                        padding: '1.5rem',
                        borderRadius: '0.875rem',
                        marginBottom: '1rem',
                        position: 'relative',
                        overflow: 'hidden',
                        wordWrap: 'break-word'
                      }}>
                        {/* Change CTA Button - Positioned in corner */}
                        <div style={{ 
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem'
                        }}>
                    <button
                            onClick={() => cycleCTAButton(position as 'beginning' | 'middle' | 'end')}
                            style={{
                              padding: '0.5rem 0.75rem',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              color: '#ffffff',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              backdropFilter: 'blur(10px)'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            title="Change to a different CTA button"
                          >
                            <RotateCcw size={12} />
                            Change CTA
                    </button>
                  </div>

                        {/* Content Layout with Logo */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                          {/* Brand logo */}
                          <div style={{
                            width: '3rem',
                            height: '3rem',
                            borderRadius: '0.75rem',
                            overflow: 'hidden',
                            flexShrink: 0,
                            backgroundColor: '#EBF212',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <svg 
                              width="36" 
                              height="36" 
                              viewBox="0 0 104 104" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ display: 'block' }}
                            >
                              <path d="M57.0805 0.247752L57.0972 38.0858C57.0995 44.0636 50.7395 47.8962 45.4553 45.1012L7.52197 25.0385C10.415 20.2771 14.0531 16.0198 18.2708 12.4222L48.0217 39.918C49.5981 41.3746 52.0832 39.7953 51.4338 37.7499L39.8963 1.41795C43.7798 0.492622 47.8304 0 51.9962 0C53.7121 0 55.4072 0.085273 57.0805 0.247752Z" fill="#000000"/>
                              <path d="M46.7872 103.741L46.7705 66.0934C46.7682 60.1156 53.1282 56.2829 58.4124 59.0779L96.3596 79.1482C93.4469 83.8969 89.7922 88.1404 85.5578 91.7213L55.846 64.2611C54.2696 62.8046 51.7845 64.3839 52.4338 66.4293L63.9277 102.624C60.0983 103.523 56.1059 104 52.0017 104C50.242 104 48.5019 103.912 46.7872 103.741Z" fill="#000000"/>
                              <path d="M64.1047 48.1799L91.6575 18.3702C88.0626 14.1359 83.8046 10.483 79.0401 7.57684L58.9208 45.6137C56.1257 50.8977 59.9591 57.258 65.937 57.2552L103.738 57.2384C103.911 55.5157 104 53.7682 104 51.9999C104 47.8878 103.521 43.8881 102.618 40.0514L66.2729 51.592C64.2275 52.2413 62.6481 49.7563 64.1047 48.1799Z" fill="#000000"/>
                              <path d="M0.245458 46.9406L37.9322 46.9239C43.9107 46.9216 47.7435 53.2814 44.9484 58.5654L24.9328 96.4064C20.1868 93.5071 15.9431 89.8674 12.3592 85.6499L39.7651 55.9991C41.2217 54.4228 39.6418 51.9377 37.5963 52.5871L1.41281 64.0764C0.490912 60.2011 0 56.1582 0 52C0 50.2928 0.0847011 48.6052 0.245458 46.9406Z" fill="#000000"/>
                            </svg>
                </div>

                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            {/* Category Header */}
                            <div style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: '600', 
                              fontFamily: 'FoundersGroteskMono, "Courier New", monospace',
                              color: '#ffffff',
                              marginBottom: '0.5rem',
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}>
                              {(ctaData as any).cta.category_header}
            </div>

                            <h4 style={{ 
                              fontSize: '1.25rem', 
                              fontWeight: '700', 
                              fontFamily: 'FoundersGrotesk, Arial, sans-serif',
                              color: '#ffffff',
                              margin: '0 0 0.75rem 0',
                              lineHeight: '1.3',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}>
                              {(ctaData as any).cta.headline}
                            </h4>

                            <p style={{ 
                              fontSize: '1rem', 
                              fontFamily: 'ABCDiatype, Inter, -apple-system, sans-serif',
                              color: '#ffffff',
                              lineHeight: '1.6',
                              margin: '0 0 1rem 0',
                              opacity: 0.9,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}>
                              {(ctaData as any).cta.description}
                            </p>

                            {/* CTA Button */}
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              padding: '0.75rem 1.5rem',
                              backgroundColor: '#BDF548',
                              color: '#192307',
                              borderRadius: '0.625rem',
                              fontSize: '1rem',
                              fontFamily: 'ABCDiatype, Inter, -apple-system, sans-serif',
                              fontWeight: '700',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }}>
                              {(ctaData as any).cta.action_button.replace(/\s*→\s*$/, '')}
                              <span style={{ fontSize: '1rem' }}>→</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* VoC Insights for this CTA */}
                      {((generatedCTAs?.position_specific_context?.[position as keyof typeof generatedCTAs.position_specific_context]) || generatedCTAs?.pain_point_context) && vocKitReady && (
                        <div style={{
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '0.5rem',
                          padding: '0.75rem',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem', 
                            marginBottom: '0.5rem' 
                          }}>
                            <div style={{
                              width: '0.75rem',
                              height: '0.75rem',
                              borderRadius: '50%',
                              backgroundColor: '#16a34a',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <CheckCircle size={8} style={{ color: 'white' }} />
                            </div>
                            <span style={{ 
                              fontSize: '0.75rem', 
                              fontWeight: '600', 
                              color: '#16a34a' 
                            }}>
                              🎯 VoC Insights Used in This {position === 'end' ? 'Ending' : position.charAt(0).toUpperCase() + position.slice(1)} CTA
                            </span>
                          </div>
                          
                          <div style={{ fontSize: '0.625rem', color: '#374151' }}>
                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>💬 Customer Language Used:</div>
                            {(generatedCTAs.position_specific_context?.[position as keyof typeof generatedCTAs.position_specific_context]?.customer_quotes || 
                              generatedCTAs.pain_point_context?.customer_quotes_used || [])?.map((quote: string, idx: number) => (
                              <div key={idx} style={{
                                fontSize: '0.6875rem',
                                fontStyle: 'italic',
                                color: '#059669',
                                backgroundColor: '#ecfdf5',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                marginBottom: '0.25rem',
                                border: '1px solid #bbf7d0'
                              }}>
                                "{quote}"
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Copy Shortcode Button */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem' 
                      }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#374151' }}>
                          Styled HTML Code:
                        </div>
              <button
                          onClick={() => copyCtaToClipboard(getStyledCtaHtml(position as 'beginning' | 'middle' | 'end'), `${position}_shortcode`)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: ctaCopySuccess === `${position}_shortcode` ? '#dcfce7' : '#6b7280',
                            color: ctaCopySuccess === `${position}_shortcode` ? '#16a34a' : 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          {ctaCopySuccess === `${position}_shortcode` ? (
                            <>
                              <CheckCircle size={10} />
                              Copied!
                  </>
                ) : (
                  <>
                              <Copy size={10} />
                              Copy
                  </>
                )}
              </button>
            </div>
                      
                      <div style={{
                        backgroundColor: '#f3f4f6',
                        padding: '0.5rem',
                        borderRadius: '0.375rem',
                        fontSize: '0.625rem',
                        fontFamily: 'monospace',
                        color: '#374151',
                        whiteSpace: 'pre-wrap',
                        border: '1px solid #e5e7eb',
                        maxHeight: '8rem',
                        overflow: 'auto',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {getStyledCtaHtml(position as 'beginning' | 'middle' | 'end')}
                      </div>

                      {/* URL Preview */}
                      <div style={{ marginTop: '0.75rem' }}>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          fontWeight: '600', 
                          color: '#374151',
                          marginBottom: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          🔗 Sign-up URL:
                        </div>
                        <div style={{
                          backgroundColor: '#eff6ff',
                          padding: '0.5rem',
                          borderRadius: '0.375rem',
                          fontSize: '0.625rem',
                          fontFamily: 'monospace',
                          color: '#1e40af',
                          border: '1px solid #bfdbfe',
                          wordBreak: 'break-all',
                          overflow: 'hidden',
                          maxWidth: '100%'
                        }}>
                          {generateUsCitrusAiSignUpUrl(row.keyword, position)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Output & Actions */}
          <div ref={rightPanelRef} className="content-modal-panel" style={{ 
            position: 'relative',
            padding: '1.5rem',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 className="mobile-only-heading" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: 0, marginBottom: '1rem' }}>
                Generated Content
              </h3>
              
              {/* Clear Button - Centered */}
              {generatedContent && (
                <button
                  onClick={clearGeneratedContent}
                  className="content-modal-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#fef2f2',
                    border: '0.0625rem solid #fecaca',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontFamily: "'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    color: '#dc2626',
                    transition: 'all 0.2s ease',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = '#dc2626';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                    e.currentTarget.style.color = '#dc2626';
                    e.currentTarget.style.borderColor = '#fecaca';
                  }}
                >
                  <X size={14} /> Clear Generated Content
                </button>
              )}
            </div>

            {/* Action Buttons */}
            {generatedContent && (
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  onClick={toggleEditMode}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: isEditingContent ? '#10b981' : '#f59e0b', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    fontFamily: "'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                                  {isEditingContent ? (<><Check size={14} /> Save Changes</>) : (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg> Edit Content</>)}
              </button>

              {/* Publish to CMS Button */}
              <button
                onClick={() => setShowCMSModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  fontFamily: "'ABCDiatype', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  minHeight: '2.75rem',
                  minWidth: '7.5rem',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#10b981')}
              >
                <Globe size={14} />
                Publish to CMS
              </button>

              {/* Open in HTML Button */}
              <button
                onClick={openInHTML}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  padding: '0.75rem 1rem', 
                  backgroundColor: '#7c3aed', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '0.5rem', 
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  minHeight: '2.75rem',
                  minWidth: '7.5rem',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#6d28d9';
                  e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#7c3aed';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="m6 16 6-12 6 12H6Z"/>
                    <path d="m8 12 8 0"/>
                  </svg>
                  Open in HTML
              </button>

              <button
                onClick={openGoogleDocs}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#84ADEA', 
                    color: 'black', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '12.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#6b96e8';
                    e.currentTarget.style.color = 'black';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#84ADEA';
                    e.currentTarget.style.color = 'black';
                  }}
                >
                  <img 
                    src="/google-docs-logo.png" 
                    alt="Google Docs"
                    style={{
                      width: '14px',
                      height: '14px',
                      objectFit: 'contain'
                    }}
                  />
                  Google Docs
                </button>

                <button
                  onClick={openGoogleSheets}
                  disabled={isOpeningSheets}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#16a34a', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: isOpeningSheets ? 'not-allowed' : 'pointer', 
                    opacity: isOpeningSheets ? 0.6 : 1,
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isOpeningSheets ? <Clock className="animate-spin" size={14} /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 9v12"/></svg>}
                  {isOpeningSheets ? 'Logging to Sheets...' : 'Open in Google Sheets'}
                </button>

                <button
                  onClick={copyToClipboard}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#6b7280', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Copy size={14} /> {showCopiedMessage ? 'Copied!' : 'Copy'}
            </button>
              </div>
            )}

            {/* Generated Content Display with Meta fields (parity with Blog modal) */}
            {generatedContent ? (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '2rem', minHeight: '25rem', backgroundColor: 'white' }}>
                {(metaSeoTitle || metaDescription) && (
                  <div style={{ marginBottom: '2rem' }}>
                                        {metaSeoTitle && (
                      <div style={{ marginBottom: '1rem', position: 'relative' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>Meta SEO Title:</strong>
                          <button
                            onClick={copyMetaTitle}
                            style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#f3f4f6', border: '1px solid #10b981', borderRadius: '0.375rem', cursor: 'pointer', color: '#10b981' }}
                          >
                            Copy
                          </button>
                          {showMetaTitleCopied && (
                            <span style={{ marginLeft: '0.5rem', color: '#10b981', fontSize: '0.75rem' }}>Copied!</span>
                          )}
                        </div>
                        <div style={{ 
                          padding: '0.75rem', 
                          backgroundColor: '#f9fafb', 
                          borderRadius: '0.375rem', 
                          border: '1px solid #e5e7eb', 
                          wordWrap: 'break-word', 
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5'
                        }}>
                          {metaSeoTitle}
                        </div>
                      </div>
                    )}
                    {metaDescription && (
                      <div style={{ marginBottom: '1rem', position: 'relative' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>Meta Description:</strong>
                          <button
                            onClick={copyMetaDescription}
                            style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: '#f3f4f6', border: '1px solid #10b981', borderRadius: '0.375rem', cursor: 'pointer', color: '#10b981' }}
                          >
                            Copy
                          </button>
                          {showMetaDescCopied && (
                            <span style={{ marginLeft: '0.5rem', color: '#10b981', fontSize: '0.75rem' }}>Copied!</span>
                          )}
                        </div>
                        <div style={{ 
                          padding: '0.75rem', 
                          backgroundColor: '#f9fafb', 
                          borderRadius: '0.375rem', 
                          border: '1px solid #e5e7eb', 
                          wordWrap: 'break-word', 
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5'
                        }}>
                          {metaDescription}
                        </div>
                      </div>
                    )}
                    <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '1.5rem 0' }} />
                  </div>
                )}
                {isEditingContent ? (
                  <div style={{ position: 'relative' }}>
                    {/* EDITING MODE Header */}
                    <div style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '0.5rem',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span style={{ 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#92400e' 
                      }}>
                        🛠️ EDITING MODE - Click "Save Changes" when done
                      </span>
                    </div>

                    {/* CTA Insertion Toolbar */}
                    {generatedCTAs && (
                      <div style={{
                        marginBottom: '1rem',
                        padding: '1rem',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '0.5rem'
                      }}>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: '600', 
                          color: '#16a34a',
                          marginBottom: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <Target size={16} />
                          Insert CTA at Cursor Position
                        </div>
                        
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: '#374151',
                          marginBottom: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          Click in the text editor where you want to insert a CTA, then click one of the buttons below:
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          {Object.entries(generatedCTAs.cta_variants).map(([position, ctaData]) => (
                            <button
                              key={position}
                              onClick={() => insertCTAAtCursor(position as 'beginning' | 'middle' | 'end')}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: ctaCopySuccess === `${position}_inserted` ? '#dcfce7' : (position === 'beginning' ? '#dbeafe' : position === 'middle' ? '#fef3c7' : '#dcfce7'),
                                color: ctaCopySuccess === `${position}_inserted` ? '#16a34a' : (position === 'beginning' ? '#1e40af' : position === 'middle' ? '#b45309' : '#16a34a'),
                                border: `1px solid ${ctaCopySuccess === `${position}_inserted` ? '#bbf7d0' : (position === 'beginning' ? '#93c5fd' : position === 'middle' ? '#fcd34d' : '#bbf7d0')}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                textTransform: 'uppercase',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              title={`Insert ${position} CTA: ${(ctaData as any).cta.headline}`}
                            >
                              {ctaCopySuccess === `${position}_inserted` ? (
                                <>
                                  <CheckCircle size={12} />
                                  Inserted!
                                </>
                              ) : (
                                <>
                                  <Target size={12} />
                                  Insert {position === 'end' ? 'ending' : position} CTA
                                </>
                              )}
                            </button>
                          ))}
                          
                          {/* CTA Count Display */}
                          {countInsertedCTAs() > 0 && (
                            <div style={{
                              padding: '0.5rem 0.75rem',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #bbf7d0',
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: '#16a34a',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem'
                            }}>
                              <CheckCircle size={12} />
                              {countInsertedCTAs()} CTA{countInsertedCTAs() !== 1 ? 's' : ''} inserted
                            </div>
                          )}
                        </div>

                        {/* Content Management Controls */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '0.5rem', 
                          marginTop: '0.75rem', 
                          paddingTop: '0.75rem',
                          borderTop: '1px solid #e5e7eb',
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}>
                          {/* Clear All CTAs Button */}
                          <button
                            onClick={clearAllCTAs}
                            disabled={countInsertedCTAs() === 0}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: countInsertedCTAs() > 0 ? '#fee2e2' : '#f3f4f6',
                              color: countInsertedCTAs() > 0 ? '#dc2626' : '#9ca3af',
                              border: `1px solid ${countInsertedCTAs() > 0 ? '#fecaca' : '#d1d5db'}`,
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: countInsertedCTAs() > 0 ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              if (countInsertedCTAs() > 0) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.backgroundColor = '#fecaca';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (countInsertedCTAs() > 0) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = '#fee2e2';
                              }
                            }}
                            title={countInsertedCTAs() > 0 ? 'Remove all inserted CTAs' : 'No CTAs to clear'}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0-1 1-2 2-2v2"/>
                              <line x1="10" y1="11" x2="10" y2="17"/>
                              <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                            Clear All CTAs
                          </button>

                          {/* Undo Button */}
                          <button
                            onClick={undoChange}
                            disabled={historyIndex <= 0}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: historyIndex > 0 ? '#eff6ff' : '#f3f4f6',
                              color: historyIndex > 0 ? '#2563eb' : '#9ca3af',
                              border: `1px solid ${historyIndex > 0 ? '#bfdbfe' : '#d1d5db'}`,
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: historyIndex > 0 ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              if (historyIndex > 0) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.backgroundColor = '#dbeafe';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (historyIndex > 0) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = '#eff6ff';
                              }
                            }}
                            title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                          >
                            <RotateCcw size={12} />
                            Undo
                          </button>

                          {/* Redo Button */}
                          <button
                            onClick={redoChange}
                            disabled={historyIndex >= editHistory.length - 1}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: historyIndex < editHistory.length - 1 ? '#f0f9ff' : '#f3f4f6',
                              color: historyIndex < editHistory.length - 1 ? '#0ea5e9' : '#9ca3af',
                              border: `1px solid ${historyIndex < editHistory.length - 1 ? '#bae6fd' : '#d1d5db'}`,
                              borderRadius: '0.375rem',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: historyIndex < editHistory.length - 1 ? 'pointer' : 'not-allowed',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              if (historyIndex < editHistory.length - 1) {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.backgroundColor = '#e0f2fe';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (historyIndex < editHistory.length - 1) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.backgroundColor = '#f0f9ff';
                              }
                            }}
                            title={historyIndex < editHistory.length - 1 ? 'Redo last undone change' : 'Nothing to redo'}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" transform="scale(-1, 1)">
                              <path d="M1 4v6h6"/>
                              <path d="m1 10 18-5v12"/>
                            </svg>
                            Redo
                          </button>
                        </div>

                        <div style={{ 
                          fontSize: '0.625rem', 
                          color: '#6b7280',
                          marginTop: '0.5rem',
                          fontStyle: 'italic'
                        }}>
                          💡 CTAs will be inserted as fully styled US Citrus AI components with immediate visual feedback.
                          <br />
                          🔗 Links will point to: uscitrusnursery.com/sign-up with UTM tracking
                        </div>
                      </div>
                    )}
                    
                    <textarea
                      ref={editableContentRef}
                      value={editableContent}
                      onChange={(e) => setEditableContent(e.target.value)}
                      style={{ width: '100%', minHeight: '37.5rem', padding: '1rem 1.25rem', border: '2px solid #f59e0b', borderRadius: '0.5rem', backgroundColor: '#fffbf5', resize: 'vertical', fontFamily: 'inherit', color: '#374151' }}
                    />
                    
                    {/* Bottom CTA Controls - Duplicate of Top Controls */}
                    {generatedCTAs && (
                        <div style={{ 
                          padding: '1rem',
                          marginTop: '1rem',
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '600', 
                            color: '#16a34a',
                            marginBottom: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <Target size={16} />
                            Insert CTA at Cursor Position
                          </div>
                          
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#374151',
                            marginBottom: '0.75rem',
                            fontStyle: 'italic'
                          }}>
                            Click in the text editor where you want to insert a CTA, then click one of the buttons below:
                          </div>

                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            {Object.entries(generatedCTAs.cta_variants).map(([position, ctaData]) => (
                              <button
                                key={position}
                                onClick={() => insertCTAAtCursor(position as 'beginning' | 'middle' | 'end')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: ctaCopySuccess === `${position}_inserted` ? '#dcfce7' : (position === 'beginning' ? '#dbeafe' : position === 'middle' ? '#fef3c7' : '#dcfce7'),
                                  color: ctaCopySuccess === `${position}_inserted` ? '#16a34a' : (position === 'beginning' ? '#1e40af' : position === 'middle' ? '#b45309' : '#16a34a'),
                                  border: `1px solid ${ctaCopySuccess === `${position}_inserted` ? '#bbf7d0' : (position === 'beginning' ? '#93c5fd' : position === 'middle' ? '#fcd34d' : '#bbf7d0')}`,
                                  borderRadius: '0.375rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  textTransform: 'uppercase',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                title={`Insert ${position} CTA: ${(ctaData as any).cta.headline}`}
                              >
                                {ctaCopySuccess === `${position}_inserted` ? (
                                  <>
                                    <CheckCircle size={12} />
                                    Inserted!
                                  </>
                                ) : (
                                  <>
                                    <Target size={12} />
                                    Insert {position === 'end' ? 'ending' : position} CTA
                                  </>
                                )}
                              </button>
                            ))}
                            
                            {/* CTA Count Display */}
                            {countInsertedCTAs() > 0 && (
                              <div style={{
                                padding: '0.5rem 0.75rem',
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem'
                              }}>
                                <CheckCircle size={12} />
                                {countInsertedCTAs()} CTA{countInsertedCTAs() !== 1 ? 's' : ''} inserted
                              </div>
                            )}
                          </div>

                          {/* Content Management Controls */}
                          <div style={{ 
                            display: 'flex', 
                            gap: '0.5rem', 
                            marginTop: '0.75rem', 
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #e5e7eb',
                            flexWrap: 'wrap',
                            alignItems: 'center'
                          }}>
                            {/* Clear All CTAs Button */}
                            <button
                              onClick={clearAllCTAs}
                              disabled={countInsertedCTAs() === 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: countInsertedCTAs() > 0 ? '#fee2e2' : '#f3f4f6',
                                color: countInsertedCTAs() > 0 ? '#dc2626' : '#9ca3af',
                                border: `1px solid ${countInsertedCTAs() > 0 ? '#fecaca' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: countInsertedCTAs() > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#fecaca';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (countInsertedCTAs() > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#fee2e2';
                                }
                              }}
                              title={countInsertedCTAs() > 0 ? 'Remove all inserted CTAs' : 'No CTAs to clear'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c0-1 1-2 2-2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                              Clear All CTAs
                            </button>

                            {/* Undo Button */}
                            <button
                              onClick={undoChange}
                              disabled={historyIndex <= 0}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex > 0 ? '#eff6ff' : '#f3f4f6',
                                color: historyIndex > 0 ? '#2563eb' : '#9ca3af',
                                border: `1px solid ${historyIndex > 0 ? '#bfdbfe' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex > 0) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                }
                              }}
                              title={historyIndex > 0 ? 'Undo last change' : 'Nothing to undo'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 4v6h6"/>
                                <path d="m1 10 18-5v12"/>
                              </svg>
                              Undo
                            </button>

                            {/* Redo Button */}
                            <button
                              onClick={redoChange}
                              disabled={historyIndex >= editHistory.length - 1}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: historyIndex < editHistory.length - 1 ? '#eff6ff' : '#f3f4f6',
                                color: historyIndex < editHistory.length - 1 ? '#2563eb' : '#9ca3af',
                                border: `1px solid ${historyIndex < editHistory.length - 1 ? '#bfdbfe' : '#d1d5db'}`,
                                borderRadius: '0.375rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: historyIndex < editHistory.length - 1 ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.backgroundColor = '#dbeafe';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (historyIndex < editHistory.length - 1) {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.backgroundColor = '#eff6ff';
                                }
                              }}
                              title={historyIndex < editHistory.length - 1 ? 'Redo last undone change' : 'Nothing to redo'}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" transform="scale(-1, 1)">
                                <path d="M1 4v6h6"/>
                                <path d="m1 10 18-5v12"/>
                              </svg>
                              Redo
                            </button>
                          </div>

                          <div style={{ 
                            fontSize: '0.625rem', 
                            color: '#6b7280',
                            marginTop: '0.5rem',
                            fontStyle: 'italic'
                          }}>
                            💡 CTAs will be inserted as fully styled US Citrus AI components with immediate visual feedback.
                            <br />
                            🔗 Links will point to: uscitrusnursery.com/sign-up with UTM tracking
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div
                    className="competitor-content-display"
                    dangerouslySetInnerHTML={{ __html: sanitizeHTMLForDisplay(getDisplayContent()) }}
                    key={`display-${isEditingContent}-${editableContent.length}`}
                  />
                )}
              </div>
            ) : (
              <div style={{ border: '2px dashed #d1d5db', borderRadius: '0.75rem', padding: '4rem 2rem', textAlign: 'center', color: '#6b7280', minHeight: '25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'white' }}>
                <div>
                  <Wand2 size={48} style={{ margin: '0 auto 1rem', opacity: 0.3, display: 'block' }} />
                  <p style={{ fontSize: '1.125rem', fontWeight: 500, marginBottom: '0.5rem' }}>No content generated yet</p>
                  <p style={{ margin: 0 }}>Generate content to see results here</p>
                </div>
              </div>
            )}

            {/* Bottom Action Buttons */}
            {generatedContent && (
              <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '0.0625rem solid #e5e7eb' }}>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {/* Edit/Save */}
                  <button
                    onClick={toggleEditMode}
                    className="content-modal-btn"
                    style={{
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem',
                      backgroundColor: isEditingContent ? '#10b981' : '#f59e0b', 
                      color: 'white', 
                      border: 'none',
                      borderRadius: '0.5rem', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                                      {isEditingContent ? (<><Check size={14} /> Save Changes</>) : (<>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    Edit Content
                  </>)}
                </button>

                {/* Publish to CMS Button */}
                <button
                  onClick={() => setShowCMSModal(true)}
                  className="content-modal-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#059669')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#10b981')}
                >
                  <Globe size={14} />
                  Publish to CMS
                </button>

                {/* Open in HTML */}
                <button
                  onClick={openInHTML}
                  className="content-modal-btn"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    padding: '0.75rem 1rem', 
                    backgroundColor: '#7c3aed', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.5rem', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    minHeight: '2.75rem',
                    minWidth: '7.5rem',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#6d28d9';
                    e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#7c3aed';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="m6 16 6-12 6 12H6Z"/>
                    <path d="m8 12 8 0"/>
                  </svg>
                  Open in HTML
                </button>

                {/* Google Docs */}
                  <button
                    onClick={openGoogleDocs}
                    className="content-modal-btn"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem', 
                      backgroundColor: '#84ADEA', 
                      color: 'black', 
                      border: 'none', 
                      borderRadius: '0.5rem', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '12.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#6b96e8';
                      e.currentTarget.style.color = 'black';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#84ADEA';
                      e.currentTarget.style.color = 'black';
                    }}
                  >
                    <img 
                      src="/google-docs-logo.png" 
                      alt="Google Docs"
                      style={{
                        width: '14px',
                        height: '14px',
                        objectFit: 'contain'
                      }}
                    />
                    Google Docs
                  </button>

                  {/* Open in Google Sheets */}
                  <button
                    onClick={openGoogleSheets}
                    disabled={isOpeningSheets}
                    className="content-modal-btn"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem', 
                      backgroundColor: '#16a34a', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0.5rem', 
                      cursor: isOpeningSheets ? 'not-allowed' : 'pointer', 
                      opacity: isOpeningSheets ? 0.6 : 1,
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isOpeningSheets ? <Clock className="animate-spin" size={14} /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 9v12"/></svg>}
                    {isOpeningSheets ? 'Logging to Sheets...' : 'Open in Google Sheets'}
                  </button>

                  {/* Copy */}
                  <button
                    onClick={copyToClipboard}
                    className="content-modal-btn"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      padding: '0.75rem 1rem', 
                      backgroundColor: '#6b7280', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0.5rem', 
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      minHeight: '2.75rem',
                      minWidth: '7.5rem',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Copy size={14} /> {showCopiedMessage ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                {/* Clear Generated Content - full width */}
                <button
                  onClick={clearGeneratedContent}
                  className="content-modal-btn"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem',
                    backgroundColor: '#fef2f2', border: '0.0625rem solid #fecaca', borderRadius: '0.5rem',
                    color: '#dc2626', cursor: 'pointer', width: '100%', justifyContent: 'center'
                  }}
                >
                  <X size={14} /> Clear Generated Content
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Close backdrop wrapper */}
      </div>

      {/* Variables Menu */}
      <VariablesMenu
        showVariablesMenu={showVariablesMenu}
        variablesButtonPosition={variablesButtonPosition}
        variablesMenuRef={variablesMenuRef}
        activePromptField={activePromptField}
        variableSearchTerm={variableSearchTerm}
        setVariableSearchTerm={setVariableSearchTerm}
        setShowVariablesMenu={setShowVariablesMenu}
        brandKit={resolvedBrandKit}
        insertVariable={insertVariable}
      />

      {/* CMS Integration Modal Component */}
      {showCMSModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '31.25rem',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Publish Content to CMS</h3>
              <button
                onClick={() => setShowCMSModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { name: 'Webflow', description: 'Publish directly to your Webflow site', logo: '/webflow-logo.png' },
                  { name: 'Strapi', description: 'Add to your Strapi content library', logo: '/strapi-logo.png' },
                  { name: 'Contentful', description: 'Create entry in Contentful space', logo: '/contenful-logo.png' },
                  { name: 'Sanity', description: 'Publish to Sanity Studio', logo: '/sanity-logo.png' },
                  { name: 'WordPress', description: 'Create WordPress post/page', logo: '/wordpress-logo.png' },
                  { name: 'Custom', description: 'Configure your own API endpoint', logo: null }
                ].map((cms) => (
                  <div
                    key={cms.name}
                    style={{
                      border: '0.0625rem solid #e5e7eb',
                      borderRadius: '0.75rem',
                      padding: '1.25rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'white',
                      boxShadow: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)'
                    }}
                    onClick={() => {
                      if (cms.name === 'Custom') {
                        setShowCustomCMSForm(true);
                        setShowCMSModal(false);
                      } else {
                        setShowComingSoonMessage(cms.name);
                        setTimeout(() => setShowComingSoonMessage(null), 3000);
                      }
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-0.0625rem)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      {cms.logo ? (
                        <div style={{
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '0.5rem',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f8fafc',
                          border: '0.0625rem solid #f1f5f9',
                          flexShrink: 0
                        }}>
                          <img 
                            src={cms.logo} 
                            alt={`${cms.name} logo`}
                            style={{
                              width: '1.5rem',
                              height: '1.5rem',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `<span style="font-size: 0.75rem; font-weight: 600; color: #64748b;">${cms.name.charAt(0)}</span>`;
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '0.5rem',
                          backgroundColor: '#6366f1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <span style={{
                            color: 'white',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {cms.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <h4 style={{ 
                        fontWeight: '600', 
                        margin: 0,
                        fontSize: '1rem',
                        color: '#111827'
                      }}>
                        {cms.name}
                      </h4>
                    </div>
                    <p style={{ 
                      color: '#6b7280', 
                      fontSize: '0.875rem', 
                      margin: 0,
                      paddingLeft: '3rem',
                      lineHeight: '1.5'
                    }}>
                      {cms.description}
                    </p>
                  </div>
                ))}
              </div>

              {/* Coming Soon Message */}
              {showComingSoonMessage && (
                <div style={{ 
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1.5rem 2rem',
                  backgroundColor: '#EBF212',
                  color: 'black',
                  borderRadius: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  boxShadow: '0 0.625rem 1.5625rem -0.1875rem rgba(0, 0, 0, 0.1), 0 0.25rem 0.375rem -0.125rem rgba(0, 0, 0, 0.05)',
                  zIndex: 10002,
                  minWidth: '15.625rem',
                  textAlign: 'center', 
                  animation: 'fadeInScale 0.2s ease-out'
                }}>
                  {showComingSoonMessage} integration coming soon!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom CMS Form Modal */}
      {showCustomCMSForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '31.25rem',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                🧈 Custom CMS Integration (Demo)
              </h3>
              <button
                onClick={() => {
                  setShowCustomCMSForm(false);
                  setPublishResult(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {!publishResult ? (
              <>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    CMS Type
                  </label>
                  <input
                    type="text"
                    value={customCMSConfig.cms_type}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, cms_type: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="e.g., Butter CMS, WordPress, Custom"
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    API Endpoint
                  </label>
                  <input
                    type="text"
                    value={customCMSConfig.api_endpoint}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, api_endpoint: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="https://api.buttercms.com/v2"
                  />
                </div>

                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    API Key
                  </label>
                  <input
                    type="text"
                    value={customCMSConfig.api_key}
                    onChange={(e) => setCustomCMSConfig({...customCMSConfig, api_key: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '0.0625rem solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                    placeholder="demo-api-key-12345"
                  />
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => publishToCustomCMS('draft')}
                    disabled={isPublishing}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: isPublishing ? 'not-allowed' : 'pointer',
                      opacity: isPublishing ? 0.6 : 1
                    }}
                  >
                    {isPublishing ? 'Publishing...' : 'Save as Draft'}
                  </button>
                  
                  <button
                    onClick={() => publishToCustomCMS('published')}
                    disabled={isPublishing}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: isPublishing ? 'not-allowed' : 'pointer',
                      opacity: isPublishing ? 0.6 : 1
                    }}
                  >
                    {isPublishing ? 'Publishing...' : 'Publish Live'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center' }}>
                {publishResult.success ? (
                  <div>
                    <div style={{ 
                      fontSize: '3rem', 
                      marginBottom: '1rem',
                      color: '#10b981'
                    }}>
                      ✅
                    </div>
                    <h4 style={{ 
                      color: '#10b981', 
                      marginBottom: '1rem',
                      fontSize: '1.25rem',
                      fontWeight: '600'
                    }}>
                      Content Published Successfully!
                    </h4>
                    {publishResult.demo_mode && (
                      <div style={{ 
                        backgroundColor: '#fef3c7', 
                        padding: '1rem', 
                        borderRadius: '0.5rem',
                        marginBottom: '1.5rem',
                        border: '0.0625rem solid #f59e0b'
                      }}>
                        <p style={{ 
                          color: '#92400e', 
                          margin: 0,
                          fontSize: '0.875rem',
                          fontWeight: '500'
                        }}>
                          🧈 Demo Mode: This is a simulated CMS publish for demonstration purposes.
                        </p>
                      </div>
                    )}
                    <div style={{ 
                      backgroundColor: '#f9fafb', 
                      padding: '1.5rem', 
                      borderRadius: '0.5rem',
                      textAlign: 'left',
                      marginBottom: '1.5rem'
                    }}>
                      <h5 style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '1rem' }}>Publication Details:</h5>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        <p style={{ margin: '0.5rem 0' }}><strong>CMS:</strong> {publishResult.cms_type}</p>
                        <p style={{ margin: '0.5rem 0' }}><strong>Status:</strong> {publishResult.status}</p>
                        <p style={{ margin: '0.5rem 0' }}><strong>ID:</strong> {publishResult.id}</p>
                        {publishResult.url && (
                          <p style={{ margin: '0.5rem 0' }}>
                            <strong>URL:</strong> 
                            <a 
                              href={publishResult.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#3b82f6', 
                                textDecoration: 'underline',
                                marginLeft: '0.5rem'
                              }}
                            >
                              {publishResult.url}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => {
                        setShowCustomCMSForm(false);
                        setPublishResult(null);
                      }}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ 
                      fontSize: '3rem', 
                      marginBottom: '1rem',
                      color: '#dc2626'
                    }}>
                      ❌
                    </div>
                    <h4 style={{ 
                      color: '#dc2626', 
                      marginBottom: '1rem',
                      fontSize: '1.25rem',
                      fontWeight: '600'
                    }}>
                      Publication Failed
                    </h4>
                    <p style={{ 
                      color: '#6b7280', 
                      marginBottom: '2rem',
                      fontSize: '0.875rem'
                    }}>
                      {publishResult.error || 'An unknown error occurred'}
                    </p>
                    
                    <button
                      onClick={() => setPublishResult(null)}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        marginRight: '1rem'
                      }}
                    >
                      Try Again
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowCustomCMSForm(false);
                        setPublishResult(null);
                      }}
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CompetitorContentActionModal;


